import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core'
import { IWindow } from '@phdsoft/window'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import { InspectionApplied } from 'src/app/automatic-points/_models/InspectionApplied'
import { MAT_DIALOG_DATA } from '@angular/material/dialog'
import { InspectionRegion } from 'src/app/automatic-points/_models/InspectionRegion'
import { InspectionSession } from 'src/app/automatic-points/_models/InspectionSession'
import { PlatesPointCriteria } from 'src/app/automatic-points/_models/PlatesPointCriteria'
import { PointsArrangements } from 'src/app/automatic-points/_models/PointsArrangements'
import { StorageService, User } from '@phdsoft/common'
import { SESSION_STORAGE, Util } from '@phdsoft/common'
import { environment } from 'src/environments/environment'
import { SignalRService } from 'src/app/signalr/signalr'
import { HttpClient } from '@angular/common/http'
import { v4 as uuid } from 'uuid'
import { AssetManager } from 'src/app/asset-manager/asset-manager'
import moment from 'moment'
import { MatSnackBar } from '@angular/material/snack-bar'
import { ClusterService } from 'src/app/cluster/cluster.service'
import { ElementResultInfo } from 'projects/phdsoft/common/src/lib/models/element-result-info.model'
import { AssetInfoViewService } from 'src/app/template/asset-info-view/asset-info-view/asset-info-view.service'
import { ClusterElementViewModel } from 'projects/phdsoft/common/src/lib/models/cluster-element.model'
import { ClusterState } from 'src/app/cluster/cluster.state'
import { ICheckList } from '../automatic-plan-type-selector/automatic-plan-type-selector.component'
import { AnomalyService } from 'src/app/admin/services/anomaly.service'
import { ActionConfig, ActionExclusionGroup, FormGroupTab, IPostRbaAnalysisConfig, IRepairCriteria, IRiskLevelConfig, IRiskSelectedColors, IRiskSelectedLevels, IRiskShieldColumnData, IRiskShieldDataRow, IRiskShieldDataTab, IUtmBracketSetingsViewModel, IUtmFacePlatesSetingsViewModel, IUtmGeneralSetingsViewModel, IUtmPlatesSettingsViewModel, IUtmStiffnersSetingsViewModel, InspectionTypeOption, RenewThicknessTypes, RepairScopeTypes, RiskLevel, RiskShieldConfiguration, TriageTableRow } from './risk-shield-config.model'
import { RiskShieldConfigService } from './risk-shield-config.service'
import { INSPECTIONS_TYPES_IDS } from 'projects/phdsoft/common/src/lib/shared/constants/inspections-types-ids'
import { INSERTIONCONSTANTS } from 'projects/phdsoft/common/src/lib/shared/constants/insertions-constants'
import { RiskLevelItem } from 'src/app/admin/pages/risk-setup/types/risk-config.model'
import { RiskLevelService } from 'src/app/admin/pages/risk-setup/service/risk-level.service'
import { IntegrityMetric, IntegrityMetricService } from 'src/app/admin/pages/risk-setup/service/integrity-metric.service'
import { StructuralDefectIds } from 'projects/phdsoft/common/src/lib/shared/constants/structuralDefectIds'
import { CrudBasicDomains } from 'projects/phdsoft/common/src/lib/models/crud-basic-domains.model'
import { CrudBasicDomainsService } from 'src/app/admin/services/crud-basic-domains.service'
import { ISideboard } from '@app/side-board/ISideboard'
import { TemplateService } from '@app/template/template.service'
import { firstValueFrom, Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

interface RSData {
  AssetId: any
  regions: any
  regionsIds: any
  sectionIds: any
  sectionNames: any
}

type TriageTableColumn = {
  id: string,
  name: string
}

interface ColumnValue {
  value: boolean;
  columnId: string;
  deadline: string;
  nextCycle: boolean;
  repeat: boolean;
  deactivateCriticalArea: boolean;
  shouldReduceRisk: boolean;
}

type StructureTypes = "Plate" | "FacePlate" | "Bracket" | "Stiffener" | undefined
type CategoriesNames = 'Improve Inspection' | 'Mitigation Actions' | 'Prescribe Repairs'
type TimeUnit = 'days' | 'months' | 'years'

export type RiskShieldSectionId = 'matrix' | 'exclusions' | 'repair' | 'points' | 'postRba' | 'scope'
type SectionStatus = 'ok' | 'warn' | 'empty'
type PendingConfirmation = 'close' | 'discard' | 'copyLevel' | null
type MatrixActionGroupId = 'inspection' | 'mitigation' | 'repair'
type MatrixActionFilter = 'all' | MatrixActionGroupId
type CellEffect = 'none' | 'lower' | 'deactivate'
type DeadlineMode = 'days' | 'next'
type BulkCopyAxis = 'row' | 'column'

interface RiskShieldSection {
  id: RiskShieldSectionId
  name: string
  description: string
}

interface SectionSummary {
  text: string
  status: SectionStatus
}

interface MatrixActionGroup {
  id: MatrixActionGroupId
  name: string
  actions: TriageTableRow[]
}

interface MatrixCauseGroup {
  id: string
  name: string
  causes: TriageTableColumn[]
}

type MatrixGridRow =
  | { kind: 'group'; group: MatrixCauseGroup }
  | { kind: 'cause'; cause: TriageTableColumn; index: number }

interface MatrixSelection {
  causeName: string
  actionId: string
}

interface ConfirmationContent {
  title: string
  text: string
  action: string
  destructive: boolean
}

interface RepairCriterionField {
  control: string
  label: string
  unit: string
  min: number
  max?: number
}

interface RepairCriteriaGroup {
  name: string
  fields: RepairCriterionField[]
}

const STRUCTURAL_DEFECT_COUNT = 3

const REPAIR_CRITERIA_GROUPS: RepairCriteriaGroup[] = [
  {
    name: 'Aggregation',
    fields: [
      { control: 'aggregationRadius', label: 'Aggregation radius', unit: 'm', min: 0 },
      { control: 'representationRadius', label: 'Representation radius', unit: 'm', min: 0 },
    ],
  },
  {
    name: 'Clearances',
    fields: [
      { control: 'minDistanceWelds', label: 'Min distance to welds', unit: 'm', min: 0 },
      { control: 'minDistanceRepairBoundariesSamePlate', label: 'Min distance, same plate', unit: 'm', min: 0 },
      { control: 'minDistanceRepairBoundariesDifferentPlates', label: 'Min distance, different plates', unit: 'm', min: 0 },
      { control: 'alignSidesRepairDistanceLessThan', label: 'Align sides if distance below', unit: 'm', min: 0 },
    ],
  },
  {
    name: 'Geometry',
    fields: [
      { control: 'maxAspectRatioRepairPlate', label: 'Max aspect ratio', unit: '', min: 1, max: 10 },
      { control: 'repairSizeIsolatedBadGaugingPoints', label: 'Isolated bad points repair size', unit: 'm²', min: 0 },
      { control: 'wholeElementAreaGreaterThan', label: 'Whole element if area above', unit: '%', min: 50, max: 100 },
    ],
  },
  {
    name: 'Discard repair if',
    fields: [
      { control: 'discardRepairAreaLessThan', label: 'Area below', unit: 'm²', min: 0 },
      { control: 'discardRepairRenewalProbabilityLessThan', label: 'Renewal probability below', unit: '%', min: 0, max: 100 },
    ],
  },
]

const REPAIR_SCOPE_LABELS: Record<string, string> = {
  '0': 'Only damaged elements',
  '1': 'Damaged and attached elements',
  '2': 'Custom table',
}

const RENEW_THICKNESS_LABELS: Record<string, string> = {
  '0': 'Original thickness',
  '1': 'Rule thickness',
}

const STATE_REFRESH_DELAY_MS = 150

@Component({
  selector: 'phdsoft-risk-shield',
  templateUrl: './risk-shield.component.html',
  styleUrls: ['./risk-shield.component.scss'],
  encapsulation: ViewEncapsulation.None,
  // OnPush keeps this huge template out of the app-wide CD cycles fired by
  // Three.js interactions (same fix as FilterComponent).
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RiskShieldComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  _AutomaticPoints: RiskShieldConfiguration
  favoriteSeason: string
  _PointsArrangements: PointsArrangements
  PointsArrangementsPlateString: string
  PointsArrangementsBracketString: string
  currentRegionIds: any[]
  isLoading = false
  isSaving = false
  selectedColors: IRiskSelectedColors = {};
  riskSelectedColors: IRiskSelectedLevels;

  readonly sections: RiskShieldSection[] = [
    { id: 'matrix', name: 'Decision Matrix', description: 'Which actions to prescribe for each risk level and cause, with deadline and effect on risk.' },
    { id: 'exclusions', name: 'Action Exclusions', description: 'Actions removed from the plan when another action is prescribed.' },
    { id: 'repair', name: 'Repair Settings', description: 'How repairs are sized and when they are discarded.' },
    { id: 'points', name: 'Inspection Points', description: 'How gauging points are generated for prescribed inspections.' },
    { id: 'postRba', name: 'Post-RBA Analysis', description: 'Analyses to run after the plan is approved.' },
    { id: 'scope', name: 'Scope', description: 'Regions and sections included in the risk-based plan.' },
  ]
  readonly navigationSections = this.sections.filter(section => section.id !== 'points')

  readonly actionFilters: { id: MatrixActionFilter; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'inspection', label: 'Inspections' },
    { id: 'mitigation', label: 'Mitigation' },
    { id: 'repair', label: 'Repairs' },
  ]

  readonly deadlineModes: { id: DeadlineMode; label: string }[] = [
    { id: 'days', label: 'Days' },
    { id: 'next', label: 'Next cycle' },
  ]

  readonly effectOptions: { id: CellEffect; label: string; hint: string }[] = [
    { id: 'none', label: 'None', hint: 'The anomaly keeps its current risk contribution.' },
    { id: 'lower', label: 'Lower risk', hint: 'When the action is done, the anomaly lowers its risk impact.' },
    { id: 'deactivate', label: 'Deactivate', hint: 'When the action is done, the anomaly stops contributing to critical area existence.' },
  ]

  readonly repairCriteriaGroups = REPAIR_CRITERIA_GROUPS

  readonly timeUnits: TimeUnit[] = ['days', 'months', 'years']

  activeSection: RiskShieldSectionId | null = null
  selectedLevelIndex = 0
  matrixActionGroups: MatrixActionGroup[] = []
  matrixCauseGroups: MatrixCauseGroup[] = []
  matrixGridRows: MatrixGridRow[] = []
  matrixVisibleActions: TriageTableRow[] = []
  matrixActionFilter: MatrixActionFilter = 'all'
  matrixCauseName: string | null = null
  matrixSelection: MatrixSelection | null = null
  levelPrescriptionCounts: Record<string, number> = {}
  bulkCopyNotice: string | null = null
  copyLevelSourceId: string | null = null
  exclusionSourceId: string | null = null
  private collapsedCauseGroups = new Set<string>()
  private matrixRows = new Map<string, Map<string, FormGroup>>()
  private actionNames = new Map<string, string>()
  summaries: Record<RiskShieldSectionId, SectionSummary>
  hasUnsavedChanges = false
  lastSavedAt: Date | null = null
  pendingConfirmation: PendingConfirmation = null
  private savedSnapshot: string | null = null
  private stateRefreshTimer: ReturnType<typeof setTimeout> | null = null

  checkLists: ICheckList[]
  categories = [
    { id: 1, name: 'Improve Inspection' },
    { id: 2, name: 'Mitigation Actions' },
    { id: 3, name: 'Prescribe Repairs' },
  ];

  colorOptions = [
    { id: 1, color: '#00ff00', label: 'Low' }, // green
    { id: 2, color: '#ffff99', label: 'Medium' }, // light yellow
    { id: 3, color: '#ffaa00', label: 'High' }, // dark yellow
    { id: 4, color: '#ff0000', label: 'Very High' }, // red
  ];

  actionExclusionGroups: ActionExclusionGroup[] = [];

  actionConfigs: ActionConfig[] = [];


  Lst_InspectionApplied: InspectionApplied[] = [
    { name: '2ND Plataform Deck 14040 a/B BL (Engine Room)', id: 1, checked: true },
    { name: 'Frame 7 (Stem)', id: 2, checked: true },
    { name: 'Frame 5 (Stem)', id: 3, checked: false },
    { name: 'Frame 4 (Stem)', id: 3, checked: true },
  ]

  Lst_InspectionAppliedPlates: InspectionApplied[] = [
    { name: '1.00', id: 1, checked: true },
    { name: '1.50', id: 2, checked: true },
    { name: '5.00', id: 4, checked: false },
    { name: '15.00', id: 6, checked: false },
    { name: '30.00', id: 8, checked: false },
    { name: '45.00', id: 10, checked: false },
  ]

  Lst_InspectionAppliedStiffeners: InspectionApplied[] = [
    { name: '1.00', id: 1, checked: true },
    { name: '1.50', id: 2, checked: true },
    { name: '5.00', id: 4, checked: false },
    { name: '15.00', id: 6, checked: false },
    { name: '30.00', id: 8, checked: false },
    { name: '45.00', id: 10, checked: false },
  ]

  Lst_InspectionAppliedFacePlates: InspectionApplied[] = [
    { name: '1.00', id: 1, checked: true },
    { name: '1.50', id: 2, checked: true },
    { name: '5.00', id: 4, checked: false },
    { name: '15.00', id: 6, checked: false },
    { name: '30.00', id: 8, checked: false },
    { name: '45.00', id: 10, checked: false },
  ]

  Lst_InspectionAppliedBrackets: InspectionApplied[] = [
    { name: '1.00', id: 1, checked: true },
    { name: '1.50', id: 2, checked: true },
    { name: '5.00', id: 4, checked: false },
    { name: '15.00', id: 6, checked: false },
    { name: '30.00', id: 8, checked: false },
    { name: '45.00', id: 10, checked: false },
  ]

  name1: String = 'Teste'
  name2: String = 'Teste2'

  LotRegion: boolean = true

  LotSection: boolean = true

  optionsWindow: ISideboard = {
    VisibleMini: true,
    VisibleClose: true,
    VisibleMaximize: true,
    Header: {
      VisibleTitle: true,
      Title: 'Risk Management',
    },
    Footer: {
      Visible: false,
    },
  }

  responsiveOptions

  //risk-shield
  actionsColumns: TriageTableColumn[]
  riskLevelsConfig: RiskLevelItem[]
  integrityMetrics: IntegrityMetric[]
  postRbaMetrics: IntegrityMetric[] = []
  selectedPostRbaProperties: string[] = []
  selectedPostRbaProperty: string = ''
  readonly FEA_METRIC_ID = 'FEA'
  readonly FEA_METRIC: IntegrityMetric = { propertyId: 'FEA', name: 'FEA', unit: '', category: 'FEA' }

  // Time unit for analysisEndOffsetDays
  analysisOffsetTimeUnit: TimeUnit = 'years'
  analysisOffsetDisplayValue: number = 0

  inspectionActions: TriageTableRow[]
  inspectionActionsForms: FormGroup[] = [];
  inspectionActionsFormsTab: FormGroupTab[] = []
  inspectionActionsFormsCount: Number = 0;
  riskInputForms: FormGroup;

  mitigationActions: TriageTableRow[]
  mitigationActionsForms: FormGroup[] = [];
  mitigationActionsFormsTab: FormGroupTab[] = []
  mitigationActionsFormsCount: Number = 0;

  allActions: TriageTableRow[]
  allActionsForms: FormGroup[] = []

  repairActions: TriageTableRow[]
  repairActionsForms: FormGroup[] = [];
  repairActionsFormsTab: FormGroupTab[] = []
  repairActionsFormsCount: Number = 0;

  tableColumnItems: any

  repairScopeForms: FormGroup
  repairScopeTypes: RepairScopeTypes

  repairCriteriaForms: FormGroup

  renewThicknessForms: FormGroup

  postRbaAnalysisForms: FormGroup

  repairSettingsTableForms: FormGroup[] = [];
  showCustomTable = false;
  repairSettingsTableColumns: StructureTypes[]
  repairSettingsTableSectionTypes = ["Bottom", "Center Line", "Longitudinal", "Main Deck"]

  inspectionTypeOptions: InspectionTypeOption[];
  inspectionTypeForm: FormGroup;

  listInspectionsFromAsset: CrudBasicDomains[]

  isMaximized: boolean = false;
  isRailCollapsed: boolean = false;

  //end risk-shield

  data: RSData

  constructor(
    private fb: FormBuilder,
    public dialog: MatDialog,
    public signalRService: SignalRService,
    public clusterService: ClusterService,
    public clusterState: ClusterState,
    public assetManager: AssetManager,
    public assetInfoViewService: AssetInfoViewService,
    private readonly storageService: StorageService,
    private snackBar: MatSnackBar,
    private readonly http: HttpClient,
    private anomalyService: AnomalyService,
    private crudService: CrudBasicDomainsService,
    private formBuilder: FormBuilder,
    private riskShieldService: RiskShieldConfigService,
    private riskLevelService: RiskLevelService,
    private integrityMetricService: IntegrityMetricService,
    private templateService: TemplateService,
    private cd: ChangeDetectorRef,
  ) {
    const AssedId = this.assetManager.asset.assetInfo.AssetId;// this.graphicsNavigatorService.graphics.assetId

    const hasSomeChecked = this.assetManager.asset.GetStructureVisibleRegions().some(x => x.visible);//this.graphicsNavigatorService.graphics.regionNavigator.some(x => x.checked);

    const regions = this.assetManager.asset.GetStructureVisibleRegions().filter(x => hasSomeChecked ? x.visible :
      x.sections.some(y => y.visible && y.group.parent.visible)).map(x => {
        if (!x.Guid) return
        const hasSomeSectionChecked = x.sections.some(y => y.visible);

        return [x.Guid, x.Name, x.sections.filter(y => hasSomeSectionChecked ? y.visible : y.visible).map(y => [
          y.Guid, y.Name
        ]),
        ]
      });

    const regionsIds = this.assetManager.asset.GetStructureVisibleRegions()
      .filter((x) => x.visible)
      .flatMap((y) => y.Guid)
    const sectionIds = this.assetManager.asset.GetStructureVisibleRegions().map(y => y.sections
      .filter((x) => x.visible)
      .flatMap((x) => x.Guid))
    const sectionNames = this.assetManager.asset.GetStructureVisibleRegions().map(y => y.sections
      .filter((x) => x.visible)
      .flatMap((x) => x.Name))

    this.data = {
      AssetId: AssedId,
      regions: regions,
      regionsIds: regionsIds,
      sectionIds: sectionIds,
      sectionNames: sectionNames,
    },


    this.isLoading = true;
    this.repairSettingsTableColumns = ['Bracket', 'FacePlate', 'Plate', 'Stiffener']


    this.responsiveOptions = [
      {
        breakpoint: '1024px',
        numVisible: 3,
        numScroll: 3,
      },
      {
        breakpoint: '768px',
        numVisible: 2,
        numScroll: 2,
      },
      {
        breakpoint: '560px',
        numVisible: 1,
        numScroll: 1,
      },
    ]

    this.assetInfoViewService.castCurrentRegionIds.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      this.currentRegionIds = data
      this.cd.markForCheck()
    })
  }

  isShown: boolean = false
  initComponent() {
    this.isShown = true
  }

  async fetchRiskLevels() {
    const customerId = this.assetManager.asset.assetInfo.CustomerId;
    if (!customerId) {
      return;
    }

    try {
      const data: RiskLevelItem[] = await this.riskLevelService.getRiskLevelItems(customerId).toPromise();
      this.riskLevelsConfig = data;
    } catch (error) {
      console.error('Error fetching risk levels:', error);
    }
  }

  async fetchIntegrityMetrics() {
    const customerId = this.assetManager.asset.assetInfo.CustomerId;
    if (!customerId) {
      return;
    }

    try {
      const data: IntegrityMetric[] = await this.integrityMetricService.getAllProperties().toPromise();

      this.integrityMetrics = data.sort((a, b) => {
        if (a.category === 'Degradation' && b.category !== 'Degradation') return -1;
        if (a.category !== 'Degradation' && b.category === 'Degradation') return 1;

        const categoryCompare = a.category.localeCompare(b.category);
        if (categoryCompare !== 0) return categoryCompare;
        
        return a.name.localeCompare(b.name);
      });

      const degradationMetrics = data.filter(m => m.category === 'Degradation');
      this.postRbaMetrics = [this.FEA_METRIC, ...degradationMetrics];
    } catch (error) {
      console.error('Error fetching risk levels:', error);
    }
  }

  public getInspectionActionsFormsGroupTabByRiskLevelConfigId(riskLevelConfigId: string): FormGroupTab | undefined {
    return this.inspectionActionsFormsTab.find(tab => tab.riskLevelConfigId === riskLevelConfigId);
  }

  public getMitigationActionsFormsGroupTabByRiskLevelConfigId(riskLevelConfigId: string): FormGroupTab | undefined {
    return this.mitigationActionsFormsTab.find(tab => tab.riskLevelConfigId === riskLevelConfigId);
  }

  public   getRepairActionsFormsGroupTabByRiskLevelConfigId(riskLevelConfigId: string): FormGroupTab | undefined {
    return this.repairActionsFormsTab.find(tab => tab.riskLevelConfigId === riskLevelConfigId);
  }

  // Ensure Post-RBA analysis values are always valid non-negative numbers
  get postRbaAnalysisValues(): IPostRbaAnalysisConfig {
    return {
      analysisEndOffsetDays: Math.max(0, Number(this.postRbaAnalysisForms.get('analysisEndOffsetDays')?.value) || 0),
      selectedProperties: this.selectedPostRbaProperties
    };
  }

  get availablePostRbaMetrics(): IntegrityMetric[] {
    return this.postRbaMetrics.filter(m => !this.selectedPostRbaProperties.includes(m.propertyId));
  }

  getSelectedPostRbaPropertyName(propertyId: string): string {
    const property = this.postRbaMetrics.find(m => m.propertyId === propertyId);
    return property?.name || propertyId;
  }

  getSelectedPostRbaPropertyCategory(propertyId: string): string {
    const property = this.postRbaMetrics.find(m => m.propertyId === propertyId);
    return property?.category || '';
  }

  addPostRbaProperty(): void {
    if (this.selectedPostRbaProperty && !this.selectedPostRbaProperties.includes(this.selectedPostRbaProperty)) {
      this.selectedPostRbaProperties.push(this.selectedPostRbaProperty);
      this.selectedPostRbaProperty = '';
    }
  }

  removePostRbaProperty(propertyId: string): void {
    const index = this.selectedPostRbaProperties.indexOf(propertyId);
    if (index > -1) {
      this.selectedPostRbaProperties.splice(index, 1);
    }
  }

  // Time unit conversion methods for analysisEndOffsetDays
  setAnalysisOffsetTimeUnit(unit: TimeUnit): void {
    const currentDays = Math.max(0, Number(this.postRbaAnalysisForms.get('analysisEndOffsetDays')?.value) || 0);
    this.analysisOffsetTimeUnit = unit;
    this.analysisOffsetDisplayValue = this.convertDaysToUnit(currentDays, this.analysisOffsetTimeUnit);
  }

  get analysisOffsetDays(): number {
    return Math.max(0, Number(this.postRbaAnalysisForms?.get('analysisEndOffsetDays')?.value) || 0);
  }

  convertDaysToUnit(days: number, targetUnit: TimeUnit): number {
    switch (targetUnit) {
      case 'days':
        return days;
      case 'months':
        return Math.round((days / 30.44) * 100) / 100;
      case 'years':
        return Math.round((days / 365.25) * 100) / 100;
    }
  }

  convertUnitToDays(value: number, fromUnit: TimeUnit): number {
    switch (fromUnit) {
      case 'days':
        return Math.round(value);
      case 'months':
        return Math.round(value * 30.44);
      case 'years':
        return Math.round(value * 365.25);
    }
  }

  onAnalysisOffsetDisplayValueChange(): void {
    const days = this.convertUnitToDays(this.analysisOffsetDisplayValue, this.analysisOffsetTimeUnit);
    this.postRbaAnalysisForms.get('analysisEndOffsetDays')?.setValue(days, { emitEvent: false });
  }

  async loadMitigations() {
    const assetId = this.assetManager.asset.assetInfo.AssetId;

    try {
      const mitigations = await firstValueFrom(
        this.anomalyService.getAnomalyActionsByAssetId(assetId)
      )

      this.mitigationActions = mitigations.map(({ name, id }) => ({ actionName: name, id }));

    } catch (error) {
      console.error('Error loading mitigations:', error);
    }
  }
  
  async loadRepairs() {
    const assetId = this.assetManager.asset.assetInfo.AssetId;

    try {
      const repairs = await firstValueFrom(
        this.anomalyService.getAnomalyRepairsByAssetId(assetId)
      )

      this.repairActions = repairs.map(({ name, id }) => ({ actionName: name, id }));

    } catch (error) {
      console.error('Error loading repairs:', error);
    }
  }

  async loadInspectionsTypes() {
    let listInspectionTypes = this.assetManager.GetAssetInspectionTypes()
    let inspectionList = await this.crudService.getCrudBasicDomainsInspectionList(listInspectionTypes).toPromise()
    let listRows = inspectionList.listRows

    this.listInspectionsFromAsset = listRows
    this.initInspectionTypeOptions(listRows)

    this.inspectionActions = listRows.map(x => { return { actionName: x.name, id: x.id } })

    this.initActionColumns(listRows)
  }

  initInspectionTypeOptions(listRows: CrudBasicDomains[]) {
    listRows.forEach(x => {
      let isPoint = x.listAssociate.some(listAssociate => listAssociate.name == "Point")
      if (isPoint) {
        let item: InspectionTypeOption = {
          id: x.id,
          label: x.name
        }
        this.inspectionTypeOptions.push(item)
      }
    })
  }

  initRiskInputForms() {
    this.riskInputForms = this.formBuilder.group({
      improveInspectionMin: [0],
      improveInspectionMax: [0],
      mitigationActionsMin: [0],
      mitigationActionsMax: [0],
      prescribeRepairsMin: [0],
      prescribeRepairsMax: [0],
    });
  }

  initRiskColors() {
    for (const category of this.categories) {
      this.selectedColors[category.id] = { 1: '', 2: '' };
    }
  }

  initInspectionTypeOptionsForm() {
    this.inspectionTypeForm = new FormGroup({
      inspectionTypeId: new FormControl('')
    });
  }

  getRiskLevelId(color: string): RiskLevel {
    for (const option of this.colorOptions) {
      if (option.color === color) {
        return option.id;
      }
    }
    return RiskLevel.None;
  }

  initRepairSettingsForms() {
    this.repairScopeForms = this.formBuilder.group({
      repairScopeControl: [RepairScopeTypes.OnlyDamaged.toString()]
    });

    this.postRbaAnalysisForms = this.formBuilder.group({
      analysisEndOffsetDays: [0, [Validators.required, Validators.min(0)]],
    });

    // Initialize display value (default to years)
    this.analysisOffsetTimeUnit = 'years';
    this.analysisOffsetDisplayValue = 0;

    // Ensure values are never null or negative by adding value transformers
    this.postRbaAnalysisForms.get('analysisEndOffsetDays')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      if (value === null || value === undefined || isNaN(value) || value < 0) {
        this.postRbaAnalysisForms.get('analysisEndOffsetDays')?.setValue(0, { emitEvent: false });
      }
      // Update display value when form value changes
      this.analysisOffsetDisplayValue = this.convertDaysToUnit(value || 0, this.analysisOffsetTimeUnit);
      this.cd.markForCheck();
    });
  }

  initRepairSettingsTableForms() {
    this.repairSettingsTableForms = [];

    this.repairSettingsTableSectionTypes.forEach(x => {
      const formGroup = this.fb.group({});

      this.repairSettingsTableColumns.forEach(column => {
        formGroup.addControl(column, this.fb.control(false));
      });

      this.repairSettingsTableForms.push(formGroup);
    });
  }

  initRepairCriteriaForms() {
    this.repairCriteriaForms = this.fb.group({
      aggregationRadius: ['1.00', Validators.required],
      representationRadius: ['0.50', Validators.required],
      minDistanceWelds: ['0.30', Validators.required],
      minDistanceRepairBoundariesSamePlate: ['1.50', Validators.required],
      minDistanceRepairBoundariesDifferentPlates: ['0.50', Validators.required],
      alignSidesRepairDistanceLessThan: ['0.50', Validators.required],
      maxAspectRatioRepairPlate: ['10', Validators.required],
      repairSizeIsolatedBadGaugingPoints: ['0.50', Validators.required],
      wholeElementAreaGreaterThan: ['90', Validators.required],
      discardRepairAreaLessThan: ['0', Validators.required],
      discardRepairRenewalProbabilityLessThan: ['0', Validators.required],
      discardRepairRemainingLifeGreaterThan: ['0', Validators.required],
      discardRepairRelativeDegradationLessThan: ['0', Validators.required],
      discardRepairCorrosionRateLessThan: ['0', Validators.required],
    });
  }

  onRepairSettingsChange(event: any): void {
    this.showCustomTable = event.value === 'custom_table';
  }

  initRenewThicknessForms() {
    this.renewThicknessForms = this.formBuilder.group({
      renewThicknessControl: [RenewThicknessTypes.Original.toString()]
    });
  }

  populateRenewThicknessFormWithData(renewThicknessTypesString: string) {
    if (renewThicknessTypesString) {
      this.renewThicknessForms.patchValue({
        renewThicknessControl: renewThicknessTypesString
      });
    } else {
      console.error('Invalid data received from API');
    }
  }

  populateInspectionTypeOptions(inspectionTypeId: string) {
    this.inspectionTypeForm.get('inspectionTypeId').setValue(inspectionTypeId);
  }

  populateRepairScopeControlWithData(repairScopeTypesString: string) {
    if (repairScopeTypesString) {
      this.repairScopeForms.patchValue({
        repairScopeControl: repairScopeTypesString
      });
    } else {
      console.error('Invalid data received from API');
    }
  }

  populateRepairCriteriaFormsWithData(data: IRepairCriteria) {
    if (data) {
      this.repairCriteriaForms.patchValue({
        aggregationRadius: data.aggregationRadius || '',
        representationRadius: data.representationRadius || '',
        minDistanceWelds: data.minDistanceWelds || '',
        minDistanceRepairBoundariesSamePlate: data.minDistanceRepairBoundariesSamePlate || '',
        minDistanceRepairBoundariesDifferentPlates: data.minDistanceRepairBoundariesDifferentPlates || '',
        alignSidesRepairDistanceLessThan: data.alignSidesRepairDistanceLessThan || '',
        maxAspectRatioRepairPlate: data.maxAspectRatioRepairPlate || '',
        repairSizeIsolatedBadGaugingPoints: data.repairSizeIsolatedBadGaugingPoints || '',
        wholeElementAreaGreaterThan: data.wholeElementAreaGreaterThan || '',
        discardRepairAreaLessThan: data.discardRepairAreaLessThan || '',
        discardRepairRenewalProbabilityLessThan: data.discardRepairRenewalProbabilityLessThan || '',
        discardRepairRemainingLifeGreaterThan: data.discardRepairRemainingLifeGreaterThan || '',
        discardRepairRelativeDegradationLessThan: data.discardRepairRelativeDegradationLessThan || '',
        discardRepairCorrosionRateLessThan: data.discardRepairCorrosionRateLessThan || '',
      });
    } else {
      console.error('Invalid data received from API');
    }
  }

  populatePostRbaAnalysisFormsWithData(data: IPostRbaAnalysisConfig) {
    if (data) {
      const days = data.analysisEndOffsetDays ?? 0;
      this.postRbaAnalysisForms.patchValue({
        analysisEndOffsetDays: days,
      });
      this.selectedPostRbaProperties = data.selectedProperties || [];
      // Initialize display value based on current unit
      this.analysisOffsetDisplayValue = this.convertDaysToUnit(days, this.analysisOffsetTimeUnit);
    } else {
      // Set default values if no data
      this.postRbaAnalysisForms.patchValue({
        analysisEndOffsetDays: 0,
      });
      this.selectedPostRbaProperties = [];
      this.analysisOffsetDisplayValue = 0;
    }
  }



  initRiskShieldInspectionActions() {
    this.inspectionActionsForms = [];

    this.inspectionActions.forEach(x => {
      const formGroup = this.fb.group({});

      this.actionsColumns.forEach(column => {
        const columnFormGroup = this.fb.group({
          value: this.fb.control(false),
          columnId: this.fb.control(column.id),
          deadline: this.fb.control(""),
          nextCycle: this.fb.control(false),
          repeat: this.fb.control(false),
          deactivateCriticalArea: this.fb.control(false),
          shouldReduceRisk: this.fb.control(false),
        });
        formGroup.addControl(column.name, columnFormGroup);
      });
      formGroup.addControl("rowId", this.fb.control(x.id));

      this.inspectionActionsForms.push(formGroup);
    });
  }

  setEmptyDecisionTableFormGroup(rows: TriageTableRow[]): FormGroup[] {
    let forms = [];

    rows.forEach(x => {
      const formGroup = this.fb.group({});

      this.actionsColumns.forEach(column => {
        const columnFormGroup = this.fb.group({
          value: this.fb.control(false),
          columnId: this.fb.control(column.id),
          deadline: this.fb.control(""),
          nextCycle: this.fb.control(false),
          repeat: this.fb.control(false),
          deactivateCriticalArea: this.fb.control(false),
          shouldReduceRisk: this.fb.control(false),
        });
        formGroup.addControl(column.name, columnFormGroup);
      });
      formGroup.addControl("rowId", this.fb.control(x.id));

      forms.push(formGroup);
    });

    return forms
  }

  setEmptyRiskShieldInspectionActions(): FormGroup[] {
    let inspectionActionsForms = [];

    this.inspectionActions.forEach(x => {
      const formGroup = this.fb.group({});

      this.actionsColumns.forEach(column => {
        const columnFormGroup = this.fb.group({
          value: this.fb.control(false),
          columnId: this.fb.control(column.id),
          deadline: this.fb.control(""),
          nextCycle: this.fb.control(false),
          repeat: this.fb.control(false),
          deactivateCriticalArea: this.fb.control(false),
          shouldReduceRisk: this.fb.control(false),
        });
        formGroup.addControl(column.name, columnFormGroup);
      });
      formGroup.addControl("rowId", this.fb.control(x.id));

      inspectionActionsForms.push(formGroup);
    });

    return inspectionActionsForms
  }

  initEmptyInspectionActionsFormsTabs(riskLevels: IRiskLevelConfig[], rows: TriageTableRow[]) {
    this.inspectionActionsFormsTab = []
    this.inspectionActionsFormsTab = this.initEmptyDecisionTableFormGroupTabs(riskLevels, rows)
  }

  initEmptyMitigationActionsFormsTabs(riskLevels: IRiskLevelConfig[], rows: TriageTableRow[]) {
    this.mitigationActionsFormsTab = []
    this.mitigationActionsFormsTab = this.initEmptyDecisionTableFormGroupTabs(riskLevels, rows)
  }

  initEmptyRepairActionsFormsTabs(riskLevels: IRiskLevelConfig[], rows: TriageTableRow[]) {
    this.repairActionsFormsTab = []
    this.repairActionsFormsTab = this.initEmptyDecisionTableFormGroupTabs(riskLevels, rows)
  }

  initEmptyDecisionTableFormGroupTabs(riskLevels: IRiskLevelConfig[], rows: TriageTableRow[]): FormGroupTab[] {
    let formGroupTabs: FormGroupTab[] = []
    if(riskLevels) {
      riskLevels.forEach(x => {
        let formGroupTab: FormGroupTab = {
          riskLevelConfigId: x.id,
          table: this.setEmptyDecisionTableFormGroup(rows)
        }
        formGroupTabs.push(formGroupTab)
      })
    } else {
      let formGroupTab: FormGroupTab = {
        riskLevelConfigId: "abc",
        table: this.setEmptyDecisionTableFormGroup(rows)
      }
      formGroupTabs = [formGroupTab]
    }
    return formGroupTabs
  }

  getRiskInputFormValueToPercentage(param: string) {
    let x = this.riskInputForms.get(param).value;
    if (x) {
      return x;
    }
    return 0
  }

  onInspectionTypeOptionsChange() {
    let inspectionTypeId = this.inspectionTypeForm.value.inspectionTypeId
    let inspectionTypeName = this.inspectionTypeOptions.find(x => x.id === inspectionTypeId).label
    this.checkLists = this.createCheckListForAutomaticPlan(inspectionTypeId)
    this._AutomaticPoints.InspectionTypeName = inspectionTypeName
    this._AutomaticPoints.InspectionTypeId = inspectionTypeId
    this._AutomaticPoints.CheckLists = this.checkLists
  }

  async saveRiskShieldConfiguration() {
    this.isSaving = true;
    const riskShieldConfig = this.buildRiskShieldConfiguration()
    const snapshot = JSON.stringify(riskShieldConfig)

    if (!riskShieldConfig.id) {
      this.riskShieldService.create(riskShieldConfig).subscribe((x) => {
        this.isSaving = false
        this._AutomaticPoints.id
        this.markAsSaved(snapshot)
      })
    }

    if (riskShieldConfig.id) {
      this.riskShieldService.update(riskShieldConfig).subscribe((x) => {
        this.isSaving = false
        this.markAsSaved(snapshot)
      })
    }
  }

  buildRiskShieldConfiguration(): RiskShieldConfiguration {
    const inspectionTriageTableTab = this.getTriageRoomTableTabData(this.inspectionActionsFormsTab)
    const mitigationTriageTableTab = this.getTriageRoomTableTabData(this.mitigationActionsFormsTab)
    const repairTriageTableTab = this.getTriageRoomTableTabData(this.repairActionsFormsTab)
    const renewThickness: RenewThicknessTypes = Number(this.renewThicknessForms.value.renewThicknessControl)
    const repairScope: RepairScopeTypes = Number(this.repairScopeForms.value.repairScopeControl)
    const repairCriteria: IRepairCriteria = this.repairCriteriaForms.value
    const riskSelectedLevels: IRiskSelectedLevels = {
      improveInspectionRiskLevel1: RiskLevel.Medium,
      improveInspectionRiskLevel2: RiskLevel.High,
      mitigationActionsRiskLevel1: RiskLevel.None,
      mitigationActionsRiskLevel2: RiskLevel.High,
      prescribeRepairsRiskLevel1: RiskLevel.None,
      prescribeRepairsRiskLevel2: RiskLevel.VeryHigh
    }
    const inspectionTypeId = this.inspectionTypeForm.value.inspectionTypeId
    const checkLists = this.checkLists
    const actionExclusionGroups = this.actionExclusionGroups
    const actionConfigs = this.actionConfigs
    const postRbaAnalysisConfig: IPostRbaAnalysisConfig = this.postRbaAnalysisValues



    // this._AutomaticPoints.PlatesSettings.PositionPointCriteriaList = this.filterEmptyValues(
    //   this._AutomaticPoints.PlatesSettings.PositionPointCriteriaList,
    // )
    // this._AutomaticPoints.StiffenersSettings.PositionPointCriteriaList = this.filterEmptyValues(
    //   this._AutomaticPoints.StiffenersSettings.PositionPointCriteriaList,
    // )
    // this._AutomaticPoints.FacePlatesSettings.PositionPointCriteriaList = this.filterEmptyValues(
    //   this._AutomaticPoints.FacePlatesSettings.PositionPointCriteriaList,
    // )
    // this._AutomaticPoints.BracketSettings.PositionPointCriteriaList =
    //   this.filterEmptyValuesBrackets(
    //     this._AutomaticPoints.BracketSettings.PositionPointCriteriaList,
    //   )

    const autoPoints = JSON.parse(JSON.stringify(this._AutomaticPoints)) as RiskShieldConfiguration
    autoPoints.Date = this.assetManager.referenceDate
    if (this.isShown) {
      const checkedRegions = []

      for (const region of autoPoints.GeneralSettings.Regions) {
        if (region.children.some((x) => x.selected)) {
          //checkedRegions.push(this.transformBack(region));
          const checkedSections = []
          for (const section of region.children) {
            if (section.selected) {
              checkedSections.push(section.id)
            }
          }

          region.SectionIds = checkedSections
          checkedRegions.push(this.transformBack(region))
        }
      }
      autoPoints.GeneralSettings.Regions = checkedRegions
    }

    const riskShieldConfig: RiskShieldConfiguration = {
      AreaSettings: autoPoints.AreaSettings,
      AssetId: autoPoints.AssetId,
      BracketSettings: autoPoints.BracketSettings,
      CheckLists: checkLists,
      Date: autoPoints.Date,
      FacePlatesSettings: autoPoints.FacePlatesSettings,
      GeneralSettings: autoPoints.GeneralSettings,
      id: autoPoints.id,
      PlatesSettings: autoPoints.PlatesSettings,
      RepairCriteria: repairCriteria,
      RepairScope: repairScope,
      StiffenersSettings: autoPoints.StiffenersSettings,
      UserId: autoPoints.UserId,
      CollectionId: autoPoints.CollectionId,
      InspectionMethodId: autoPoints.InspectionMethodId,
      InspectionTypeId: inspectionTypeId,
      InspectionTypeName: autoPoints.InspectionTypeName,
      Label: autoPoints.Label,
      RenewThickness: renewThickness,
      InspectionTriageTableTab: inspectionTriageTableTab,
      MitigationTriageTableTab: mitigationTriageTableTab,
      RepairTriageTableTab: repairTriageTableTab,
      RiskSelectedLevels: riskSelectedLevels,
      ExecuteRiskShield: true,
      ActionExclusionGroups: actionExclusionGroups,
      ActionConfigs: actionConfigs,
      PostRbaAnalysisConfig: postRbaAnalysisConfig
    }

    return riskShieldConfig
  }

  initRiskShieldMitigationActions() {
    this.mitigationActionsForms = [];

    this.mitigationActions.forEach(x => {
      const formGroup = this.fb.group({});

      this.actionsColumns.forEach(column => {
        const columnFormGroup = this.fb.group({
          value: this.fb.control(false),
          columnId: this.fb.control(column.id),
          deadline: this.fb.control(""),
          nextCycle: this.fb.control(false),
          repeat: this.fb.control(false),
          deactivateCriticalArea: this.fb.control(false),
          shouldReduceRisk: this.fb.control(false),
        });
        formGroup.addControl(column.name, columnFormGroup);
      });
      formGroup.addControl("rowId", this.fb.control(x.id));

      this.mitigationActionsForms.push(formGroup);
    });
  }

  onPlatesPointsArrangementsChange(event: any) {
    if (!event) {
      // Set a default value if event is undefined/null
      this._AutomaticPoints.PlatesSettings.PointsArrangements = PointsArrangements.OnVertices;
      return;
    }

    const arrangement = Number(event);
    if (!isNaN(arrangement) && arrangement in PointsArrangements) {
      this._AutomaticPoints.PlatesSettings.PointsArrangements = arrangement;
    } else {
      this._AutomaticPoints.PlatesSettings.PointsArrangements = PointsArrangements.OnVertices;
    }
  }

  onBracketsPointsArrangementsChange(event: any) {
    if (!event) {
      // Set a default value if event is undefined/null
      this._AutomaticPoints.BracketSettings.PointsArrangement = PointsArrangements.OnVertices;
      return;
    }

    const arrangement = Number(event);
    if (!isNaN(arrangement) && arrangement in PointsArrangements) {
      this._AutomaticPoints.BracketSettings.PointsArrangement = arrangement;
    } else {
      this._AutomaticPoints.BracketSettings.PointsArrangement = PointsArrangements.OnVertices;
    }
  }


  getTriageRoomTableData(formGroup: FormGroup[]): IRiskShieldDataRow[] {
    let formGroupValue = formGroup.map(x => {
      return x.value;
    });

    let formGroupFiltered: IRiskShieldDataRow[] = formGroupValue.map(x => {
      let columns: IRiskShieldColumnData[] = [];
      let rowId: string;

      Object.entries(x).forEach(([name, valueObj]: [string, ColumnValue | string]) => {

        if (name === "rowId") {
          rowId = valueObj as string;
        } else {
          const { columnId, value, deadline, nextCycle, repeat, shouldReduceRisk, deactivateCriticalArea } = valueObj as ColumnValue;
          columns.push({ Name: name, ColumnId: columnId, Value: value, Deadline: deadline, NextCycle: nextCycle, Repeat: repeat, ShouldReduceRisk: shouldReduceRisk, DeactivateCriticalArea: deactivateCriticalArea });
        }
      });

      return { RowId: rowId, Columns: columns };
    });


    return formGroupFiltered;
  }

  getTriageRoomTableTabData(formGroupTab: FormGroupTab[]): IRiskShieldDataTab[] {
    let riskShieldDataTabs: IRiskShieldDataTab[] = []

    formGroupTab.forEach(x => {
      let riskShieldDataTab: IRiskShieldDataTab = {
        RiskLevelConfigId: x.riskLevelConfigId,
        RiskShieldDataRow: this.getTriageRoomTableData(x.table)
      }
      riskShieldDataTabs.push(riskShieldDataTab)
    })

    return riskShieldDataTabs
  }


  createCheckListForAutomaticPlan(inspectionTypeId: string): ICheckList[] {
    const inspectionType = this.listInspectionsFromAsset.find(x => x.id == inspectionTypeId)
    if (inspectionType) {
      return inspectionType.listRows.map(x => {
        return {
          Id: uuid() as string,
          StatusId: '',
          InputTypeId: x.inputTypeId,
          Label: x.name,
          Name: null,
          Value: '',
          Comments: x.Comments,
          Degree: 0,
          Recommendations: '',
          Description: null,
          PhotoExplanation: null,
          Date: this.assetManager.referenceDate,
          Attachments: [] as string[],
          Measurements: [] as string[],
          AttributesList: [] as string[],
          InspectionSubTypeId: x.id
        }
      })
    }
    return null;
  }

  getSelectedColorsFormatted(selectedColors: IRiskSelectedColors) {
    const result: IRiskSelectedLevels = {
      improveInspectionRiskLevel1: null,
      improveInspectionRiskLevel2: null,
      mitigationActionsRiskLevel1: null,
      mitigationActionsRiskLevel2: null,
      prescribeRepairsRiskLevel1: null,
      prescribeRepairsRiskLevel2: null,
    };

    for (const category of this.categories) {
      const selectedColor = selectedColors[category.id];
      const riskLevel1 = this.getRiskLevelId(selectedColor[1]);
      const riskLevel2 = this.getRiskLevelId(selectedColor[2]);

      switch (category.name as CategoriesNames) {
        case 'Improve Inspection':
          result.improveInspectionRiskLevel1 = riskLevel1;
          result.improveInspectionRiskLevel2 = riskLevel2;
          break;
        case 'Mitigation Actions':
          result.mitigationActionsRiskLevel1 = riskLevel1;
          result.mitigationActionsRiskLevel2 = riskLevel2;
          break;
        case 'Prescribe Repairs':
          result.prescribeRepairsRiskLevel1 = riskLevel1;
          result.prescribeRepairsRiskLevel2 = riskLevel2;
          break;
      }
    }
    return result;
  }

  populateSelectedColors(data: IRiskSelectedLevels) {
    let result: IRiskSelectedColors = {}
    for (const category of this.categories) {
      switch (category.name as CategoriesNames) {
        case 'Improve Inspection':
          result[category.id] = [
            this.colorOptions.find((option) => option.id === data.improveInspectionRiskLevel1)?.color || '',
            this.colorOptions.find((option) => option.id === data.improveInspectionRiskLevel2)?.color || '',
          ];
          break;
        case 'Mitigation Actions':
          result[category.id] = [
            this.colorOptions.find((option) => option.id === data.mitigationActionsRiskLevel1)?.color || '',
            this.colorOptions.find((option) => option.id === data.mitigationActionsRiskLevel2)?.color || '',
          ];
          break;
        case 'Prescribe Repairs':
          result[category.id] = [
            this.colorOptions.find((option) => option.id === data.prescribeRepairsRiskLevel1)?.color || '',
            this.colorOptions.find((option) => option.id === data.prescribeRepairsRiskLevel2)?.color || '',
          ];
          break;
      }
    }

    this.selectedColors = result
  }

  populateSelectedColors2(data: IRiskSelectedLevels): IRiskSelectedColors {
    const result: IRiskSelectedColors = {};

    for (const category of this.categories) {
      const categoryId = category.id;
      let color1: string = '';
      let color2: string = '';
      switch (category.name as CategoriesNames) {
        case 'Improve Inspection':
          color1 = this.colorOptions.find((option) => option.id === data.improveInspectionRiskLevel1)?.color || '';
          color2 = this.colorOptions.find((option) => option.id === data.improveInspectionRiskLevel2)?.color || '';
          break;
        case 'Mitigation Actions':
          color1 = this.colorOptions.find((option) => option.id === data.mitigationActionsRiskLevel1)?.color || '';
          color2 = this.colorOptions.find((option) => option.id === data.mitigationActionsRiskLevel2)?.color || '';
          break;
        case 'Prescribe Repairs':
          color1 = this.colorOptions.find((option) => option.id === data.prescribeRepairsRiskLevel1)?.color || '';
          color2 = this.colorOptions.find((option) => option.id === data.prescribeRepairsRiskLevel2)?.color || '';
          break;
      }

      result[categoryId] = {
        1: color1,
        2: color2
      };
    }

    return result;
  }

  populateDecisionTableFormGroup(data: IRiskShieldDataRow[], rows: TriageTableRow[]): FormGroup[] {
    const formGroups: FormGroup[] = [];

    rows.forEach(row => {
      const rowData = data.find(d => d.RowId === row.id);
      const rowObj: { [key: string]: any } = {};

      this.actionsColumns.forEach(column => {
        const columnData = rowData?.Columns.find(c => c.ColumnId === column.id);
        const columnGroup = this.fb.group({
          value: columnData ? columnData.Value : false,
          columnId: columnData ? columnData.ColumnId : column.id,
          deadline: columnData ? columnData.Deadline : '',
          nextCycle: columnData ?  columnData.NextCycle : false,
          repeat: columnData ? columnData.Repeat : false,
          deactivateCriticalArea: columnData ? columnData.DeactivateCriticalArea : false,
          shouldReduceRisk: columnData ? columnData.ShouldReduceRisk : false,
        });

        rowObj[column.name] = columnGroup;
      });

      rowObj['rowId'] = row.id;

      const formGroup = this.fb.group(rowObj);
      formGroups.push(formGroup);
    });

    return formGroups;
  }

  populateFormTabs(data: IRiskShieldDataTab[], rows: TriageTableRow[], riskLevelsConfig: IRiskLevelConfig[]): FormGroupTab[] {
    const formGroupTables: FormGroupTab[] = [];

    riskLevelsConfig.forEach(riskLevelConfig => {
      const tabData = data.find(tab => tab.RiskLevelConfigId === riskLevelConfig.id);
      const formGroups = tabData
        ? this.populateDecisionTableFormGroup(tabData.RiskShieldDataRow, rows)
        : this.setEmptyDecisionTableFormGroup(rows);

      const formGroupTable: FormGroupTab = {
        riskLevelConfigId: riskLevelConfig.id,
        table: formGroups
      };
      formGroupTables.push(formGroupTable);
    });

    return formGroupTables;
  }

  initRiskShieldRepairActions() {
    this.repairActionsForms = [];

    this.repairActions.forEach(x => {
      const formGroup = this.fb.group({});

      this.actionsColumns.forEach(column => {
        const columnFormGroup = this.fb.group({
          value: this.fb.control(false),
          columnId: this.fb.control(column.id),
          deadline: this.fb.control(""),
          nextCycle: this.fb.control(false),
          repeat: this.fb.control(false),
          deactivateCriticalArea: this.fb.control(false),
          shouldReduceRisk: this.fb.control(false),
        });
        formGroup.addControl(column.name, columnFormGroup);
      });
      formGroup.addControl("rowId", this.fb.control(x.id));

      this.repairActionsForms.push(formGroup);
    });
  }



  initializeComponentData() {

    // this._AutomaticPoints.id = null
    // this._AutomaticPoints.AssetId = this.data.AssetId
    // this._AutomaticPoints.Label = 'AutomaticPoints'
    // this._AutomaticPoints.InspectionTypeId = this.data.automatiPlanTypeSelectorData.InspectionTypeId
    // this._AutomaticPoints.InspectionMethodId = this.data.automatiPlanTypeSelectorData.InspectionMethodId
    // this._AutomaticPoints.InspectionTypeName = this.data.automatiPlanTypeSelectorData.InspectionTypeName
    // this._AutomaticPoints.CheckLists = this.data.automatiPlanTypeSelectorData.CheckLists

    this.favoriteSeason = 'Pattern'

    //#region "GENERAL SETTINGS"
    const GeneralSettings: IUtmGeneralSetingsViewModel = {
      BulkheadsNoStifffners: true,
      DeleteOldAutomaticPoints: false,
      Date: moment(this.assetManager.referenceDate).format('yyyy-MM-DD',),
      Regions: []
    }

    for (let i = 0; i <= this.data.regions.length - 1; i++) {
      let _Lst_SectionIds = []

      var _ItemRegions = new InspectionRegion()
      _ItemRegions.RegionId = this.data.regions[i][0]
      _ItemRegions.SectionIds = this.data.regions[i][2].map((x) => x[0])
      _ItemRegions.RegionName = this.data.regions[i][1]
      _ItemRegions.RegionChecked = !Util.isNullOrUndefined(
        this.data.regionsIds.find((x) => x === this.data.regions[i][0]),
      )
      if (!this.isShown) {
        _ItemRegions.RegionChecked = true
      }

      var _Info_Session = new InspectionSession()
      _ItemRegions.Sections = []

      for (let j = 0; j <= this.data.regions[i][2].length - 1; j++) {
        const sectionInfo = {
          SectionId: this.data.regions[i][2][j][0],
          SectionName: this.data.regions[i][2][j][1],
          SectionChecked: !Util.isNullOrUndefined(
            this.data.sectionIds.find((x) => x === this.data.regions[i][2][j][0]),
          ),
        }
        if (!this.isShown) {
          sectionInfo.SectionChecked = true
        }

        _Lst_SectionIds.push(this.data.regions[i][2][j][0])

        _ItemRegions.Sections.push(sectionInfo)
      }

      if (this.isShown) {
        GeneralSettings.Regions.push(this.tranform(_ItemRegions))
      } else {
        GeneralSettings.Regions.push(_ItemRegions)
      }
    }
    //#endregion

    //#region  : PLATES

    let _PlatesPointCriteria: PlatesPointCriteria[] = [
      { Area: 0.09, NumberOfPoints: 1, AddCenterPoint: true, NumberOfRows: 0 },
      { Area: 0.5, NumberOfPoints: 2, AddCenterPoint: true, NumberOfRows: 0 }  ,
      { Area: 1.0, NumberOfPoints: 4, AddCenterPoint: true, NumberOfRows: 0 },
      { Area: 0, NumberOfPoints: 0, AddCenterPoint: false, NumberOfRows: 0 },
      { Area: 0, NumberOfPoints: 0, AddCenterPoint: false, NumberOfRows: 0 },
      { Area: 0, NumberOfPoints: 0, AddCenterPoint: false, NumberOfRows: 0 },
    ]

    let _PointsArrangements: PointsArrangements
    if (this.favoriteSeason == 'Linear') {
      _PointsArrangements = PointsArrangements.Linear
    } else if (this.favoriteSeason == 'AscendingDiagonal') {
      _PointsArrangements = PointsArrangements.AscendingDiagonal
    } else if (this.favoriteSeason == 'DescendingDiagonal') {
      _PointsArrangements = PointsArrangements.DescendingDiagonal
    } else if (this.favoriteSeason == 'ZigZag') {
      _PointsArrangements = PointsArrangements.Pattern
    } else if (this.favoriteSeason == 'OnVertices') {
      _PointsArrangements = PointsArrangements.ZigZag
    } else {
      _PointsArrangements = PointsArrangements.Pattern
    }
    this.PointsArrangementsPlateString = _PointsArrangements.toString();

    const PlatesSettings: IUtmPlatesSettingsViewModel = {
      PositionGaugingsforPlates: true,
      PositionGaugingsforBays: false,
      PositionGaugingsforCriticalAreas: false,
      CriticalAreaThreshold: 0,
      PositionGaugingsforAnomalies: false,
      AnomaliesThreshold: 0,
      PositionPointCriteriaList: _PlatesPointCriteria,
      PointsArrangements: _PointsArrangements
    }



    //#endregion

    //#region : "STIFFENERS"

    let _PositionPointCriteriaListStiffeners = [
      { Length: 0.9, NumberOfPoints: 1 },
      { Length: 4, NumberOfPoints: 2 },
      { Length: 12, NumberOfPoints: 3 },
      { Length: 0, NumberOfPoints: 0 },
      { Length: 0, NumberOfPoints: 0 },
      { Length: 0, NumberOfPoints: 0 },
    ]

    const StiffenersSettings: IUtmStiffnersSetingsViewModel = {
      PositionGaugingPointsOnStiffeners: true,
      PositionGaugingPointsOnBays: false,
      InsertPointsOnWeb: true,
      InsertPointsOnFlange: true,
      PositionGaugingsforCriticalAreas: false,
      CriticalAreaThreshold: 0,
      PositionPointCriteriaList: _PositionPointCriteriaListStiffeners
    }

    //#endregion

    //#region "FACE PLATES"

    let _PositionPointCriteriaListFacePlates = [
      { Length: 0.01, NumberOfPoints: 1 },
      { Length: 5, NumberOfPoints: 3 },
      { Length: 0, NumberOfPoints: 0 },
      { Length: 0, NumberOfPoints: 0 },
      { Length: 0, NumberOfPoints: 0 },
      { Length: 0, NumberOfPoints: 0 },
    ]

    const FacePlatesSettings: IUtmFacePlatesSetingsViewModel = {
      PositionGaugingPointsOnFacePlates: true,
      PositionGaugingPointsOnBays: false,
      PositionGaugingsforCriticalAreas: false,
      CriticalAreaThreshold: 0,
      PositionPointCriteriaList: _PositionPointCriteriaListFacePlates
    }

    //#endregion

    //#region "BRACKETS"

    let _PositionPointCriteriaListBrackets = [
      { Area: 0.5, NumberOfPointsWeb: 1, NumberOfPointsFlange: 1 },
      { Area: 5, NumberOfPointsWeb: 3, NumberOfPointsFlange: 3 },
      { Area: 10, NumberOfPointsWeb: 4, NumberOfPointsFlange: 4 },
      { Area: 0, NumberOfPointsWeb: 0, NumberOfPointsFlange: 0 },
    ]

    const BracketSettings: IUtmBracketSetingsViewModel = {
      PositionGaugingPointsOnBrackets: true,
      PositionGaugingPointsOnBays: false,
      PositionGaugingsforCriticalAreas: false,
      CriticalAreaThreshold: 0,
      PositionPointCriteriaList: _PositionPointCriteriaListBrackets,
      PointsArrangement: _PointsArrangements
    }

    //#endregion

    const defaultRepairCriteria: IRepairCriteria = {
      aggregationRadius: '',
      representationRadius: '',
      minDistanceWelds: '',
      minDistanceRepairBoundariesSamePlate: '',
      minDistanceRepairBoundariesDifferentPlates: '',
      alignSidesRepairDistanceLessThan: '',
      maxAspectRatioRepairPlate: '',
      repairSizeIsolatedBadGaugingPoints: '',
      wholeElementAreaGreaterThan: '',
      discardRepairAreaLessThan: '',
      discardRepairRenewalProbabilityLessThan: '',
      discardRepairRemainingLifeGreaterThan: '',
      discardRepairRelativeDegradationLessThan: '',
      discardRepairCorrosionRateLessThan: '',
    };

    const defaultRiskSelectedLevels: IRiskSelectedLevels = {
      improveInspectionRiskLevel1: RiskLevel.Medium,
      improveInspectionRiskLevel2: RiskLevel.High,
      mitigationActionsRiskLevel1: RiskLevel.None,
      mitigationActionsRiskLevel2: RiskLevel.High,
      prescribeRepairsRiskLevel1: RiskLevel.None,
      prescribeRepairsRiskLevel2: RiskLevel.VeryHigh,
    };

    this.selectedColors = this.populateSelectedColors2(defaultRiskSelectedLevels)

    const riskShieldConfig: RiskShieldConfiguration = {
      id: null,
      CollectionId: null,
      AssetId: this.data.AssetId,
      Label: 'AutomaticPoints',
      BracketSettings: BracketSettings,
      FacePlatesSettings: FacePlatesSettings,
      StiffenersSettings: StiffenersSettings,
      PlatesSettings: PlatesSettings,
      GeneralSettings: GeneralSettings,
      AreaSettings: { AreaForCriticalAreas: false },
      CheckLists: [],
      Date: this.assetManager.referenceDate,
      UserId: null,
      InspectionTypeId: null,
      InspectionTypeName: null,
      InspectionMethodId: null,
      RepairCriteria: defaultRepairCriteria,
      RepairScope: RepairScopeTypes.OnlyDamaged,
      RenewThickness: RenewThicknessTypes.Original,
      InspectionTriageTableTab: [],
      MitigationTriageTableTab: [],
      RepairTriageTableTab: [],
      RiskSelectedLevels: defaultRiskSelectedLevels,
      ExecuteRiskShield: true,
      ActionExclusionGroups: [],
      ActionConfigs: [],
      PostRbaAnalysisConfig: { analysisEndOffsetDays: 0, selectedProperties: [] }
    }

    return riskShieldConfig;
  }

  tranform = (region) => ({
    name: region.RegionName,
    id: region.RegionId,
    selected: false,
    parentId: '',
    item: '',
    children: region.Sections.map((y) => ({
      name: y.SectionName,
      parentId: region.RegionId,
      selected: false,
      id: y.SectionId,
    })),
  })

  transformBack = (region) => ({
    SectionIds: region.SectionIds,
    RegionName: region.name,
    RegionId: region.id,
    RegionChecked: region.selected,
    Sections: region.children.map((x) => ({
      SectionName: x.name,
      SectionId: x.id,
      SectionChecked: x.selected,
    })),
  })

  onRiskColorChange(categoryId: number, colorOptionId: number, event: any) {
    if (!this.selectedColors[categoryId]) {
      this.selectedColors[categoryId] = {};
    }
    this.selectedColors[categoryId][colorOptionId] = event.target.value;
  }
  async ngOnInit() {
    this.initComponent();

    // Independent HTTP loads run in parallel to shorten the form's opening time.
    // loadInspectionsTypes stays sequential: it pushes into actionsColumns,
    // which is reassigned below after fetchIntegrityMetrics resolves.
    await Promise.all([
      this.fetchIntegrityMetrics(),
      this.loadMitigations(),
      this.loadRepairs(),
      this.fetchRiskLevels(),
    ])

    this.actionsColumns = [
      { id: StructuralDefectIds.BUCKLING, name: 'Buckling' },
      { id: StructuralDefectIds.FATIGUE, name: 'Fatigue' },
      { id: StructuralDefectIds.YIELDING, name: 'Yielding' },
      // { id: "4", name: 'Corrosion Rate' },
      // { id: "5", name: 'Relative Degradation' },
      // { id: "6", name: 'Remaining Life' },
    ];

    this.integrityMetrics.forEach((metric) => {
      this.actionsColumns.push({
        id: metric.propertyId,
        name: metric.name,
      });
    });

    this.inspectionTypeOptions = []

    this.initRiskInputForms();
    this.initRiskColors();
    await this.loadInspectionsTypes();
    await this.loadConfiguration()

    this.allActions = [...this.inspectionActions, ...this.mitigationActions, ...this.repairActions];

    this.mitigationActionsFormsCount = this.mitigationActionsForms.length
    this.inspectionActionsFormsCount = this.inspectionActionsForms.length
    this.repairActionsFormsCount = this.repairActionsForms.length

    this.tableColumnItems = [
      {
        colspan: this.inspectionActionsFormsCount,
        name: 'Improve Inspections',
        styleClass: 'inspection-actions-table inspection-actions-table-title'
      },
      {
        colspan: this.mitigationActionsFormsCount,
        name: 'Mitigation Actions',
        styleClass: 'mitigation-actions-table mitigation-actions-table-title'
      },
      {
        colspan: this.repairActionsFormsCount,
        name: 'Prescribe Repairs',
        styleClass: 'repair-actions-table repair-actions-table-title'
      }
    ]

    this.initMatrixStructure()

    this.isLoading = false;
    this.markAsSaved(JSON.stringify(this.buildRiskShieldConfiguration()), false)
    // Async continuation: under OnPush the view is not marked dirty by the
    // awaits above, so the loaded form would never render without this.
    this.cd.markForCheck();
  }

  ngOnDestroy(): void {
    this.clearStateRefreshTimer()
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async loadConfiguration() {
    this.actionExclusionGroups = []
    this.actionConfigs = []
    this.selectedPostRbaProperties = []
    this.selectedPostRbaProperty = ''
    this.checkLists = undefined
    this.PointsArrangementsBracketString = undefined
    this.initInspectionTypeOptionsForm()
    this.initEmptyInspectionActionsFormsTabs(this.riskLevelsConfig, this.inspectionActions)
    this.initEmptyMitigationActionsFormsTabs(this.riskLevelsConfig, this.mitigationActions)
    this.initEmptyRepairActionsFormsTabs(this.riskLevelsConfig, this.repairActions)
    this.initRiskShieldInspectionActions();
    this.initRiskShieldMitigationActions();
    this.initRiskShieldRepairActions();
    this.initRepairSettingsTableForms();
    this.initRepairSettingsForms()
    this.initRenewThicknessForms()
    this.initRepairCriteriaForms()
    this._AutomaticPoints = await this.fillConfigurations();
    this.indexMatrixRows()
  }

  async discardChanges() {
    this.pendingConfirmation = null
    this.clearStateRefreshTimer()
    this.isLoading = true
    await this.loadConfiguration()
    this.isLoading = false
    this.markAsSaved(JSON.stringify(this.buildRiskShieldConfiguration()), false)
  }

  get activeSectionIndex(): number {
    return this.navigationSections.findIndex(section => section.id === this.activeSection)
  }

  get activeSectionInfo(): RiskShieldSection | undefined {
    return this.sections.find(section => section.id === this.activeSection)
  }

  get saveStatusText(): string {
    if (this.isSaving) return 'Saving...'
    if (this.hasUnsavedChanges) return 'Unsaved changes'
    if (this.lastSavedAt) return `Saved at ${moment(this.lastSavedAt).format('HH:mm')}`
    return 'No changes'
  }

  openSection(sectionId: RiskShieldSectionId) {
    this.activeSection = sectionId
  }

  openAdjacentSection(offset: number) {
    const next = this.navigationSections[this.activeSectionIndex + offset]
    if (next) {
      this.activeSection = next.id
    }
  }

  backToOverview() {
    this.refreshState()
    this.activeSection = null
  }

  scheduleStateRefresh() {
    this.clearStateRefreshTimer()
    this.stateRefreshTimer = setTimeout(() => {
      this.stateRefreshTimer = null
      this.refreshState()
    }, STATE_REFRESH_DELAY_MS)
  }

  get confirmation(): ConfirmationContent | null {
    switch (this.pendingConfirmation) {
      case 'close':
        return {
          title: 'Discard unsaved changes?',
          text: 'Closing now loses the changes made since the last save.',
          action: 'Discard and close',
          destructive: true,
        }
      case 'discard':
        return {
          title: 'Discard unsaved changes?',
          text: 'The settings go back to the last saved version.',
          action: 'Discard',
          destructive: true,
        }
      case 'copyLevel': {
        const source = this.getLevelName(this.copyLevelSourceId)
        const target = this.activeLevel?.name ?? ''
        return {
          title: `Replace ${target} prescriptions?`,
          text: `Every cause and action in ${target} takes the prescription, deadline and effect set in ${source}.`,
          action: 'Replace',
          destructive: false,
        }
      }
      default:
        return null
    }
  }

  confirmPendingAction() {
    switch (this.pendingConfirmation) {
      case 'close':
        this.pendingConfirmation = null
        this.templateService.toggleRiskShield(false)
        return
      case 'copyLevel':
        this.pendingConfirmation = null
        this.copyLevelPrescriptions()
        return
      default:
        this.discardChanges()
    }
  }

  cancelPendingAction() {
    this.pendingConfirmation = null
    this.copyLevelSourceId = null
  }

  private markAsSaved(snapshot: string, updateTimestamp = true) {
    this.savedSnapshot = snapshot
    if (updateTimestamp) {
      this.lastSavedAt = new Date()
    }
    this.refreshState()
  }

  private refreshState() {
    if (this.isLoading || !this._AutomaticPoints) {
      return
    }
    this.clearStateRefreshTimer()
    this.hasUnsavedChanges = JSON.stringify(this.buildRiskShieldConfiguration()) !== this.savedSnapshot
    this.levelPrescriptionCounts = this.countLevelPrescriptions()
    this.summaries = {
      matrix: this.getMatrixSummary(),
      exclusions: this.getExclusionsSummary(),
      repair: this.getRepairSummary(),
      points: this.getPointsSummary(),
      postRba: this.getPostRbaSummary(),
      scope: this.getScopeSummary(),
    }
    this.cd.markForCheck()
  }

  private clearStateRefreshTimer() {
    if (this.stateRefreshTimer) {
      clearTimeout(this.stateRefreshTimer)
      this.stateRefreshTimer = null
    }
  }

  private getMatrixSummary(): SectionSummary {
    let prescriptions = 0
    const levelsWithPrescriptions = new Set<string>()
    const tabs = [...this.inspectionActionsFormsTab, ...this.mitigationActionsFormsTab, ...this.repairActionsFormsTab]
    tabs.forEach(tab => {
      tab.table.forEach(formGroup => {
        Object.entries(formGroup.value).forEach(([name, cell]: [string, ColumnValue | string]) => {
          if (name !== 'rowId' && (cell as ColumnValue).value) {
            prescriptions++
            levelsWithPrescriptions.add(tab.riskLevelConfigId)
          }
        })
      })
    })
    if (!prescriptions) {
      return { text: 'No prescriptions', status: 'empty' }
    }
    const levels = levelsWithPrescriptions.size
    return {
      text: `${prescriptions} ${prescriptions === 1 ? 'prescription' : 'prescriptions'} in ${levels} ${levels === 1 ? 'level' : 'levels'}`,
      status: 'ok',
    }
  }

  private getExclusionsSummary(): SectionSummary {
    const rules = this.actionExclusionGroups.reduce((total, group) => total + group.excludedActionIds.length, 0)
    return rules
      ? { text: `${rules} ${rules === 1 ? 'exclusion' : 'exclusions'}`, status: 'ok' }
      : { text: 'No exclusions', status: 'empty' }
  }

  private getRepairSummary(): SectionSummary {
    const scope = REPAIR_SCOPE_LABELS[this.repairScopeForms.value.repairScopeControl] ?? 'No repair scope'
    const thickness = RENEW_THICKNESS_LABELS[this.renewThicknessForms.value.renewThicknessControl] ?? 'No renew thickness'
    const warnings = this.repairWarningCount
    if (warnings) {
      return { text: `${warnings} ${warnings === 1 ? 'value' : 'values'} out of range · ${scope}`, status: 'warn' }
    }
    return { text: `${scope} · ${thickness}`, status: 'ok' }
  }

  private getPointsSummary(): SectionSummary {
    const settings = this._AutomaticPoints
    const structuresOn = [
      settings.PlatesSettings?.PositionGaugingsforPlates,
      settings.StiffenersSettings?.PositionGaugingPointsOnStiffeners,
      settings.FacePlatesSettings?.PositionGaugingPointsOnFacePlates,
      settings.BracketSettings?.PositionGaugingPointsOnBrackets,
    ].filter(Boolean).length
    const inspectionTypeId = this.inspectionTypeForm.value.inspectionTypeId
    const inspectionType = this.inspectionTypeOptions.find(option => option.id === inspectionTypeId)
    if (!inspectionType) {
      return { text: `No inspection type · ${structuresOn} of 4 structures`, status: 'warn' }
    }
    return { text: `${inspectionType.label} · ${structuresOn} of 4 structures`, status: 'ok' }
  }

  private getPostRbaSummary(): SectionSummary {
    const metrics = this.selectedPostRbaProperties.length
    if (!metrics) {
      return { text: 'No analysis selected', status: 'empty' }
    }
    const offset = `${this.analysisOffsetDisplayValue} ${this.analysisOffsetTimeUnit}`
    return { text: `${metrics} ${metrics === 1 ? 'analysis' : 'analyses'} · after ${offset}`, status: 'ok' }
  }

  private getScopeSummary(): SectionSummary {
    const regions = this._AutomaticPoints.GeneralSettings.Regions ?? []
    let sections = 0
    let regionsWithSelection = 0
    regions.forEach(region => {
      const selected = (region.children ?? []).filter(section => section.selected).length
      sections += selected
      if (selected) regionsWithSelection++
    })
    if (!sections) {
      return { text: 'No section selected', status: 'warn' }
    }
    return {
      text: `${sections} ${sections === 1 ? 'section' : 'sections'} in ${regionsWithSelection} ${regionsWithSelection === 1 ? 'region' : 'regions'}`,
      status: 'ok',
    }
  }

  async initActionColumns(listRows: CrudBasicDomains[]) {
    listRows.forEach(x => {
      x.listRows.forEach(y => {
        if (y.inputTypeId == "yes/no" || y.inputTypeId == "ok/notok" || y.inputTypeId == "list") {
          let item: TriageTableColumn = {
            id: y.id,
            name: y.name,
          }
          this.actionsColumns.push(item)
        }
      })
    })
  }

  async fillConfigurations() {
    const configs = await this.riskShieldService.getByAssetId(this.data.AssetId).toPromise();

    if (!configs) {
      return this.initializeComponentData()
    }

    if (configs) {
      const GeneralSettings: IUtmGeneralSetingsViewModel = {
        BulkheadsNoStifffners: configs.GeneralSettings.BulkheadsNoStifffners,
        DeleteOldAutomaticPoints: configs.GeneralSettings.DeleteOldAutomaticPoints,
        Date: configs.GeneralSettings.Date,
        Regions: []
      }

      for (let i = 0; i <= this.data.regions.length - 1; i++) {
        let _Lst_SectionIds = []

        var _ItemRegions = new InspectionRegion()
        _ItemRegions.RegionId = this.data.regions[i][0]
        _ItemRegions.SectionIds = this.data.regions[i][2].map((x) => x[0])
        _ItemRegions.RegionName = this.data.regions[i][1]
        _ItemRegions.RegionChecked = !Util.isNullOrUndefined(
          this.data.regionsIds.find((x) => x === this.data.regions[i][0]),
        )
        if (!this.isShown) {
          _ItemRegions.RegionChecked = true
        }

        var _Info_Session = new InspectionSession()
        _ItemRegions.Sections = []

        for (let j = 0; j <= this.data.regions[i][2].length - 1; j++) {
          const sectionInfo = {
            SectionId: this.data.regions[i][2][j][0],
            SectionName: this.data.regions[i][2][j][1],
            SectionChecked: !Util.isNullOrUndefined(
              this.data.sectionIds.find((x) => x === this.data.regions[i][2][j][0]),
            ),
          }
          if (!this.isShown) {
            sectionInfo.SectionChecked = true
          }

          _Lst_SectionIds.push(this.data.regions[i][2][j][0])

          _ItemRegions.Sections.push(sectionInfo)
        }

        if (this.isShown) {
          GeneralSettings.Regions.push(this.tranform(_ItemRegions))
        } else {
          GeneralSettings.Regions.push(_ItemRegions)
        }
      }

      let _PlatesPointCriteria: PlatesPointCriteria[] = [
        { Area: 0.09, NumberOfPoints: 1, AddCenterPoint: true, NumberOfRows: 0 },
        { Area: 0.5, NumberOfPoints: 2, AddCenterPoint: true, NumberOfRows: 0 },
        { Area: 1.0, NumberOfPoints: 4, AddCenterPoint: true, NumberOfRows: 0 },
        { Area: 0, NumberOfPoints: 0, AddCenterPoint: false, NumberOfRows: 0 },
        { Area: 0, NumberOfPoints: 0, AddCenterPoint: false, NumberOfRows : 0 },
      ]

      const PlatesSettings: IUtmPlatesSettingsViewModel = {
        PositionGaugingsforPlates: configs.PlatesSettings.PositionGaugingsforPlates,
        PositionGaugingsforBays: configs.PlatesSettings.PositionGaugingsforBays,
        PositionGaugingsforCriticalAreas: configs.PlatesSettings.PositionGaugingsforCriticalAreas,
        CriticalAreaThreshold: configs.PlatesSettings.CriticalAreaThreshold,
        PositionGaugingsforAnomalies: configs.PlatesSettings.PositionGaugingsforAnomalies,
        AnomaliesThreshold: configs.PlatesSettings.AnomaliesThreshold,
        PositionPointCriteriaList: configs.PlatesSettings.PositionPointCriteriaList,
        PointsArrangements: configs.PlatesSettings.PointsArrangements
      }
      this.PointsArrangementsPlateString = configs.PlatesSettings.PointsArrangements.toString();

      const StiffenersSettings: IUtmStiffnersSetingsViewModel = {
        PositionGaugingPointsOnStiffeners: configs.StiffenersSettings.PositionGaugingPointsOnStiffeners,
        PositionGaugingPointsOnBays: configs.StiffenersSettings.PositionGaugingPointsOnBays,
        InsertPointsOnWeb: configs.StiffenersSettings.InsertPointsOnWeb,
        InsertPointsOnFlange: configs.StiffenersSettings.InsertPointsOnFlange,
        PositionGaugingsforCriticalAreas: configs.StiffenersSettings.PositionGaugingsforCriticalAreas,
        CriticalAreaThreshold: configs.StiffenersSettings.CriticalAreaThreshold,
        PositionPointCriteriaList: configs.StiffenersSettings.PositionPointCriteriaList
      }

      const FacePlatesSettings: IUtmFacePlatesSetingsViewModel = {
        PositionGaugingPointsOnFacePlates: configs.FacePlatesSettings.PositionGaugingPointsOnFacePlates,
        PositionGaugingPointsOnBays: configs.FacePlatesSettings.PositionGaugingPointsOnBays,
        PositionGaugingsforCriticalAreas: configs.FacePlatesSettings.PositionGaugingsforCriticalAreas,
        CriticalAreaThreshold: configs.FacePlatesSettings.CriticalAreaThreshold,
        PositionPointCriteriaList: configs.FacePlatesSettings.PositionPointCriteriaList
      }

      const BracketSettings: IUtmBracketSetingsViewModel = {
        PositionGaugingPointsOnBrackets: configs.BracketSettings.PositionGaugingPointsOnBrackets,
        PositionGaugingPointsOnBays: configs.BracketSettings.PositionGaugingPointsOnBays,
        PositionGaugingsforCriticalAreas: configs.BracketSettings.PositionGaugingsforCriticalAreas,
        CriticalAreaThreshold: configs.BracketSettings.CriticalAreaThreshold,
        PositionPointCriteriaList: configs.BracketSettings.PositionPointCriteriaList,
        PointsArrangement: configs.BracketSettings.PointsArrangement
      }
      this.PointsArrangementsBracketString = configs.BracketSettings.PointsArrangement.toString();


      //#endregion

      const repairCriteria: IRepairCriteria = {
        ...configs.RepairCriteria,
      };

      const riskSelectedLevels: IRiskSelectedLevels = {
        ...configs.RiskSelectedLevels,
      }

      let inspectionTypeName
      if (configs.InspectionTypeId) {
        inspectionTypeName = this.inspectionTypeOptions.find(x => x.id === configs.InspectionTypeId).label
      }

      let actionExclusionGroups: ActionExclusionGroup[]
      if(configs.ActionExclusionGroups){
        actionExclusionGroups = configs.ActionExclusionGroups
      }

      let actionConfigs: ActionConfig[] = []
      // if(configs.ActionConfigs) {
      //   actionConfigs = configs.ActionConfigs
      // }

      const riskShieldConfig: RiskShieldConfiguration = {
        id: configs.id,
        CollectionId: configs.CollectionId,
        AssetId: configs.AssetId,
        Label: configs.Label,
        BracketSettings: BracketSettings,
        FacePlatesSettings: FacePlatesSettings,
        StiffenersSettings: StiffenersSettings,
        PlatesSettings: PlatesSettings,
        GeneralSettings: GeneralSettings,
        AreaSettings: { AreaForCriticalAreas: false },
        CheckLists: configs.CheckLists,
        Date: this.assetManager.referenceDate,
        UserId: configs.UserId,
        InspectionTypeId: configs.InspectionTypeId,
        InspectionTypeName: inspectionTypeName,
        InspectionMethodId: null,
        RepairCriteria: repairCriteria,
        RepairScope: configs.RepairScope,
        RenewThickness: configs.RenewThickness,
        InspectionTriageTableTab: configs.InspectionTriageTableTab,
        MitigationTriageTableTab: configs.MitigationTriageTableTab,
        RepairTriageTableTab: configs.RepairTriageTableTab,
        RiskSelectedLevels: riskSelectedLevels,
        ExecuteRiskShield: true,
        ActionExclusionGroups: actionExclusionGroups,
        ActionConfigs: actionConfigs,
        PostRbaAnalysisConfig: configs.PostRbaAnalysisConfig ?? { analysisEndOffsetDays: 0, selectedProperties: [] }
      }

      if (riskShieldConfig) {
        if (riskShieldConfig.RenewThickness) {
          this.populateRenewThicknessFormWithData(riskShieldConfig.RenewThickness.toString());
        }
        if (riskShieldConfig.RepairScope) {
          this.populateRepairScopeControlWithData(riskShieldConfig.RepairScope.toString());
        }
        // if(riskShieldConfig.RiskInput){
        //   this.populateRiskInputFormsWithData(riskShieldConfig.RiskInput);
        // }
        if (riskShieldConfig.RepairCriteria) {
          this.populateRepairCriteriaFormsWithData(riskShieldConfig.RepairCriteria);
        }
        if (riskShieldConfig.PostRbaAnalysisConfig) {
          this.populatePostRbaAnalysisFormsWithData(riskShieldConfig.PostRbaAnalysisConfig);
        }
        if (riskShieldConfig.InspectionTriageTableTab) {
          // this.inspectionActionsForms = this.populateDecisionTableFormGroup(riskShieldConfig.InspectionTriageTable, this.inspectionActions);
          // let riskShieldDataTab: IRiskShieldDataTab[] = [{
          //   RiskLevelConfigId: "abc",
          //   RiskShieldDataRow: riskShieldConfig.InspectionTriageTable
          // }]
          this.inspectionActionsFormsTab = this.populateFormTabs(riskShieldConfig.InspectionTriageTableTab, this.inspectionActions, this.riskLevelsConfig)
        }
        if (riskShieldConfig.MitigationTriageTableTab) {
          // this.mitigationActionsForms = this.populateDecisionTableFormGroup(riskShieldConfig.MitigationTriageTable, this.mitigationActions);
          // let riskShieldDataTab: IRiskShieldDataTab[] = [{
          //   RiskLevelConfigId: "abc",
          //   RiskShieldDataRow: riskShieldConfig.MitigationTriageTable
          // }]
          this.mitigationActionsFormsTab = this.populateFormTabs(riskShieldConfig.MitigationTriageTableTab, this.mitigationActions, this.riskLevelsConfig)
        }
        if (riskShieldConfig.RepairTriageTableTab) {
          // this.repairActionsForms = this.populateDecisionTableFormGroup(riskShieldConfig.RepairTriageTable, this.repairActions);
          this.repairActionsFormsTab = this.populateFormTabs(riskShieldConfig.RepairTriageTableTab, this.repairActions, this.riskLevelsConfig)
        }
        if (riskShieldConfig.RiskSelectedLevels) {
          this.selectedColors = this.populateSelectedColors2(riskShieldConfig.RiskSelectedLevels)
        }
        if (riskShieldConfig.InspectionTypeId) {
          this.populateInspectionTypeOptions(riskShieldConfig.InspectionTypeId)
        }
        if(riskShieldConfig.ActionExclusionGroups) {
          this.actionExclusionGroups = riskShieldConfig.ActionExclusionGroups
        }

        if(riskShieldConfig.ActionConfigs) {
          this.actionConfigs = riskShieldConfig.ActionConfigs
        }
      }

      // this.getDataFromApi().subscribe((data) => {
      //   this.populateSelectedColors(data);
      // });

      return riskShieldConfig
    }

  }

  private filterEmptyValues(values) {
    const indexesToRemove = []
    let index = 0
    for (const iterator of values) {
      if (Util.isNullOrUndefined(iterator.NumberOfPoints)) {
        indexesToRemove.push(index)
      }
      index++
    }

    for (let i = indexesToRemove.length - 1; i >= 0; i--) {
      values.splice(indexesToRemove[i], 1)
    }
    return values
  }

  private filterEmptyValuesBrackets(values) {
    const indexesToRemove = []
    let index = 0
    for (const iterator of values) {
      if (Util.isNullOrUndefined(iterator.NumberOfPointsWeb)) {
        indexesToRemove.push(index)
      }
      index++
    }

    for (let i = indexesToRemove.length - 1; i >= 0; i--) {
      values.splice(indexesToRemove[i], 1)
    }
    return values
  }

  async submit() {
    this._AutomaticPoints.ExecuteRiskShield = true;
    this._AutomaticPoints.InspectionMethodId = INSERTIONCONSTANTS.POINT;
    const autoPoints = JSON.parse(JSON.stringify(this._AutomaticPoints)) as RiskShieldConfiguration
    autoPoints.Date = this.assetManager.referenceDate
    if (this.isShown) {
      const checkedRegions = []

      for (const region of autoPoints.GeneralSettings.Regions) {
        if (region.children.some((x) => x.selected)) {
          //checkedRegions.push(this.transformBack(region));
          const checkedSections = []
          for (const section of region.children) {
            if (section.selected) {
              checkedSections.push(section.id)
            }
          }

          region.SectionIds = checkedSections
          checkedRegions.push(this.transformBack(region))
        }
      }
      autoPoints.GeneralSettings.Regions = checkedRegions
    }

    const user = (await this.storageService.getItem<any>(SESSION_STORAGE.USER)) as User
    autoPoints.UserId = user.id
    autoPoints.CollectionId = uuid().replace(/-/g, '') as string
    let _response = await this.http
      .post<any>(
        environment.API_INSPEC + 'UtmPoints/signalrutmautomaticinspectionpoints',
        autoPoints,
      )
      .toPromise()

    let elementToRegenerate = <ElementResultInfo>{}
    let elementToRegenerateArray = <ElementResultInfo[]>{}
    let checklist = <any>{}
    checklist.Date = autoPoints.Date
    elementToRegenerate.AssetId = autoPoints.AssetId
    elementToRegenerate.RegionIds = []
    var ids: any[]
    ids = autoPoints.GeneralSettings.Regions.map((x) => x.RegionId)
    ids.forEach((x) => elementToRegenerate.RegionIds.push(x))

    elementToRegenerate.CheckList = []
    elementToRegenerate.CheckList.push(checklist)
    elementToRegenerateArray = []
    elementToRegenerateArray.push(elementToRegenerate)
    const data: Array<ClusterElementViewModel> = new Array<ClusterElementViewModel>()
    for (const iterator of elementToRegenerateArray) {
      let datesFromChecklist = iterator.CheckList.map((x) => x.Date)
      let uniqueDatesFromChecklist: any[] = [...new Set(datesFromChecklist)]

      let datesFromChecklistNotClusterized: any[] = []
      if (this.clusterState.ClusterState.items.length == 0) {
        datesFromChecklistNotClusterized =
          datesFromChecklistNotClusterized.concat(uniqueDatesFromChecklist)
      } else {
        uniqueDatesFromChecklist.forEach((dateFromChecklist) => {
          let test = this.clusterState.ClusterState.items.find(
            (item) =>
              // TODO change this InspectionTypeId hardcoded later
              item.date === dateFromChecklist &&
              item.typeId === INSPECTIONS_TYPES_IDS.THICKNESS_GAUGING,
          )
          if (test == undefined) {
            datesFromChecklistNotClusterized.push(dateFromChecklist)
          }
        })
      }
      datesFromChecklistNotClusterized = [...new Set(datesFromChecklistNotClusterized)]
      if (datesFromChecklistNotClusterized.length > 0) {
        // TODO change this InspectionTypeId hardcoded later
        data.push({
          ClusterType: 'Inspection',
          AssetId: autoPoints.AssetId,
          RegionIds: ids,
          Dates: uniqueDatesFromChecklist,
          InspectionTypeId: INSPECTIONS_TYPES_IDS.THICKNESS_GAUGING,
        })
      }
    }

    if (data.length > 0) {
      // Cluster regeneration with UTM is activated in the backend
      // this.clusterService.RegenerateClustersIfNeeded(data).toPromise();
    }

    for (const iterator of elementToRegenerateArray) {
      let datesFromChecklist = iterator.CheckList.map((x) => x.Date)
      let uniqueDatesFromChecklist: any[] = [...new Set(datesFromChecklist)]
      uniqueDatesFromChecklist.forEach((x) => {
        this.clusterState.checkClusterState({ date: x, typeId: iterator.InspectionTypeId })
      })
    }

    //workaround soon this will be changed
  }

  Execute() {
    this.dialog.closeAll()
  }

  //#region "CHECKBOX CONTROL CHECKED - PLATES BY SESSION STRUCTURE"

  _FacePlates_Session1_Chk1Changed(checked) {
    this._AutomaticPoints.FacePlatesSettings.PositionGaugingPointsOnBays = false
  }

  _FacePlates_Session1_Chk2Changed(checked) {
    this._AutomaticPoints.FacePlatesSettings.PositionGaugingPointsOnFacePlates = false
  }

  //#endregion

  onSelectRegion({ regionId }) {
    this._AutomaticPoints.GeneralSettings.Regions.forEach((element) => {
      if (element.id === regionId) {
        var selected = !element.selected

        element.selected = selected

        element.children.forEach((s) => {
          s.selected = selected
        })
      }
    })
  }

  onSelectSection({ regionId, sectionId }) {
    this._AutomaticPoints.GeneralSettings.Regions.forEach((element) => {
      if (element.id === regionId) {
        element.children.forEach((s) => {
          if (s.id === sectionId) {
            s.selected = !Boolean(s.selected)
          }
        })
      }
    })
  }

  isActionExcluded(prescribedActionId: string, excludedActionId: string): boolean {
    const exclusionGroup = this.actionExclusionGroups.find(group => group.prescribedActionId === prescribedActionId);
    return exclusionGroup?.excludedActionIds.includes(excludedActionId) || false;
  }

  onExcludedActionChange(prescribedActionId: string, excludedActionId: string): void {
    let exclusionGroup = this.actionExclusionGroups.find(group => group.prescribedActionId === prescribedActionId);

    if (!exclusionGroup) {
      exclusionGroup = { prescribedActionId, excludedActionIds: [] };
      this.actionExclusionGroups.push(exclusionGroup);
    }

    const index = exclusionGroup.excludedActionIds.indexOf(excludedActionId);
    if (index > -1) {
      // If the action is already excluded, remove it
      exclusionGroup.excludedActionIds.splice(index, 1);
    } else {
      // If the action is not excluded, add it
      exclusionGroup.excludedActionIds.push(excludedActionId);
    }

    // Remove the group if there are no excluded actions
    if (exclusionGroup.excludedActionIds.length === 0) {
      const index = this.actionExclusionGroups.indexOf(exclusionGroup);
      if (index > -1) {
        this.actionExclusionGroups.splice(index, 1);
      }
    }
  }

  // isDeactivateCriticalAreaEnabled(actionId: string): boolean {
  //   const config = this.actionConfigs.find(config => config.actionId === actionId);
  //   return config?.deactivateCriticalArea || false;
  // }

  // toggleDeactivateCriticalArea(actionId: string): void {
  //   let config = this.actionConfigs.find(config => config.actionId === actionId);

  //   if (!config) {
  //     config = { actionId, deactivateCriticalArea: false };
  //     this.actionConfigs.push(config);
  //   }

  //   config.deactivateCriticalArea = !config.deactivateCriticalArea;

  //   // Remove the config if both properties are false
  //   if (!config.deactivateCriticalArea) {
  //     const index = this.actionConfigs.indexOf(config);
  //     if (index > -1) {
  //       this.actionConfigs.splice(index, 1);
  //     }
  //   }
  // }

  onExclusiveChange(formGroup: FormGroup, columnName: string, changedControl: 'deactivateCriticalArea' | 'shouldReduceRisk'): void {
    const isChecked = formGroup.get(`${columnName}.${changedControl}`).value;

    // If the checkbox is being checked, uncheck the other one
    if (isChecked) {
      const otherControl = changedControl === 'deactivateCriticalArea' ? 'shouldReduceRisk' : 'deactivateCriticalArea';
      formGroup.get(`${columnName}.${otherControl}`).setValue(false);

      // Set the value control to true when either checkbox is checked
      // formGroup.get(`${columnName}.value`).setValue(true);
    } else {
      // If both checkboxes are unchecked, set value to false
      const otherControl = changedControl === 'deactivateCriticalArea' ? 'shouldReduceRisk' : 'deactivateCriticalArea';
      const otherIsChecked = formGroup.get(`${columnName}.${otherControl}`).value;

      // if (!otherIsChecked) {
      //   formGroup.get(`${columnName}.value`).setValue(false);
      // }
    }
  }

  #endregion

  onDeadlineChange(formGroup: FormGroup, columnName: string) {
    const deadline = formGroup.get(`${columnName}.deadline`).value;
    const nextCycle: boolean = formGroup.get(`${columnName}.nextCycle`).value;
    if (deadline != "" || deadline == 0 || nextCycle) {
      formGroup.get(`${columnName}.value`).setValue(true);
    }
  }

  handleMaximize(isMaximized: boolean) {
    this.isMaximized = isMaximized;
    if (isMaximized) {
      this.isRailCollapsed = true

      if (!this.activeSection) {
        this.activeSection = this.navigationSections[0].id
      }
    }
  }

  handleCancel() {
    this.refreshState()
    if (this.hasUnsavedChanges) {
      this.pendingConfirmation = 'close'
      return
    }
    this.templateService.toggleRiskShield(false)
  }

  requestDiscard() {
    this.pendingConfirmation = 'discard'
  }

  // Decision matrix

  get matrixLevels(): RiskLevelItem[] {
    return this.riskLevelsConfig ?? []
  }

  get activeLevel(): RiskLevelItem | undefined {
    return this.matrixLevels[this.selectedLevelIndex]
  }

  get matrixCauseIndex(): number {
    return this.actionsColumns.findIndex(cause => cause.name === this.matrixCauseName)
  }

  get selectedCell(): FormGroup | null {
    return this.matrixSelection ? this.getCell(this.matrixSelection.actionId, this.matrixSelection.causeName) : null
  }

  get selectedActionName(): string {
    return this.matrixSelection ? this.getActionName(this.matrixSelection.actionId) : ''
  }

  selectLevel(index: number) {
    this.selectedLevelIndex = index
    this.bulkCopyNotice = null
  }

  getLevelName(levelId: string | null): string {
    return this.matrixLevels.find(level => level.id === levelId)?.name ?? ''
  }

  getCell(actionId: string, causeName: string, levelId = this.activeLevel?.id): FormGroup | null {
    return (this.matrixRows.get(levelId)?.get(actionId)?.get(causeName) as FormGroup) ?? null
  }

  getDeadlineControl(cell: FormGroup): FormControl {
    return cell.get('deadline') as FormControl
  }

  isPrescribed(cell: FormGroup | null): boolean {
    return !!cell?.value.value
  }

  hasDeadline(cell: FormGroup): boolean {
    const { deadline, nextCycle } = cell.value
    return !!nextCycle || (deadline !== '' && deadline !== null && deadline !== undefined)
  }

  getDeadlineMode(cell: FormGroup): DeadlineMode {
    return cell.value.nextCycle ? 'next' : 'days'
  }

  getCellEffect(cell: FormGroup): CellEffect {
    if (cell.value.deactivateCriticalArea) return 'deactivate'
    if (cell.value.shouldReduceRisk) return 'lower'
    return 'none'
  }

  getEffectHint(cell: FormGroup): string {
    const effect = this.getCellEffect(cell)
    return this.effectOptions.find(option => option.id === effect)?.hint ?? ''
  }

  getCellSummary(cell: FormGroup): string {
    const { deadline, nextCycle, repeat } = cell.value
    const parts: string[] = []
    if (nextCycle) {
      parts.push('Next cycle')
    } else {
      parts.push(this.hasDeadline(cell) ? `${deadline} d` : 'No deadline')
    }
    if (repeat && this.hasDeadline(cell)) parts.push('repeat')
    const effect = this.getCellEffect(cell)
    if (effect === 'lower') parts.push('lower risk')
    if (effect === 'deactivate') parts.push('deactivate')
    return parts.join(' · ')
  }

  getCellGlyph(cell: FormGroup | null): string {
    if (!this.isPrescribed(cell)) return '·'
    const { deadline, nextCycle, repeat } = cell.value
    let glyph = nextCycle ? 'NC' : this.hasDeadline(cell) ? `${deadline}d` : '✓'
    if (repeat && this.hasDeadline(cell)) glyph += ' ↻'
    const effect = this.getCellEffect(cell)
    if (effect === 'lower') glyph += ' ↓'
    if (effect === 'deactivate') glyph += ' ⊘'
    return glyph
  }

  getCellDescription(cause: TriageTableColumn, action: TriageTableRow): string {
    const cell = this.getCell(action.id, cause.name)
    const state = this.isPrescribed(cell) ? this.getCellSummary(cell) : 'not prescribed'
    return `${cause.name}, ${action.actionName}: ${state}`
  }

  countPrescribedActions(causeName: string): number {
    return this.allActions.filter(action => this.isPrescribed(this.getCell(action.id, causeName))).length
  }

  setPrescribed(cell: FormGroup, prescribed: boolean) {
    if (prescribed) {
      cell.patchValue({ value: true })
      return
    }
    cell.patchValue({
      value: false,
      deadline: '',
      nextCycle: false,
      repeat: false,
      shouldReduceRisk: false,
      deactivateCriticalArea: false,
    })
  }

  setDeadlineMode(cell: FormGroup, mode: DeadlineMode) {
    if (mode === 'next') {
      cell.patchValue({ nextCycle: true, deadline: '' })
    } else {
      cell.patchValue({ nextCycle: false })
    }
  }

  setRepeat(cell: FormGroup, repeat: boolean) {
    cell.patchValue({ repeat })
  }

  setEffect(cell: FormGroup, effect: CellEffect) {
    cell.patchValue({
      shouldReduceRisk: effect === 'lower',
      deactivateCriticalArea: effect === 'deactivate',
    })
  }

  setMatrixCause(causeName: string) {
    this.matrixCauseName = causeName
    this.matrixSelection = null
    this.bulkCopyNotice = null
  }

  openAdjacentCause(offset: number) {
    const causes = this.actionsColumns
    const index = (this.matrixCauseIndex + offset + causes.length) % causes.length
    this.setMatrixCause(causes[index].name)
  }

  isCompactActionOpen(actionId: string): boolean {
    return this.matrixSelection?.actionId === actionId && this.matrixSelection?.causeName === this.matrixCauseName
  }

  toggleCompactAction(actionId: string) {
    this.matrixSelection = this.isCompactActionOpen(actionId) ? null : { actionId, causeName: this.matrixCauseName }
  }

  onCompactPrescribeChange(actionId: string, prescribed: boolean) {
    const cell = this.getCell(actionId, this.matrixCauseName)
    if (!cell) return
    this.setPrescribed(cell, prescribed)
    this.matrixSelection = prescribed ? { actionId, causeName: this.matrixCauseName } : null
  }

  setActionFilter(filter: MatrixActionFilter) {
    this.matrixActionFilter = filter
    this.rebuildMatrixGrid()
  }

  isCauseGroupCollapsed(groupId: string): boolean {
    return this.collapsedCauseGroups.has(groupId)
  }

  toggleCauseGroup(groupId: string) {
    if (this.collapsedCauseGroups.has(groupId)) {
      this.collapsedCauseGroups.delete(groupId)
    } else {
      this.collapsedCauseGroups.add(groupId)
    }
    this.rebuildMatrixGrid()
  }

  isActionGroupStart(action: TriageTableRow): boolean {
    return this.matrixActionGroups.some(group => group.actions[0] === action)
  }

  isGridCellSelected(cause: TriageTableColumn, action: TriageTableRow): boolean {
    return this.matrixSelection?.causeName === cause.name && this.matrixSelection?.actionId === action.id
  }

  isGridCellTabStop(cause: TriageTableColumn, action: TriageTableRow, rowIndex: number, columnIndex: number): boolean {
    return this.matrixSelection ? this.isGridCellSelected(cause, action) : rowIndex === 0 && columnIndex === 0
  }

  selectGridCell(cause: TriageTableColumn, action: TriageTableRow) {
    const cell = this.getCell(action.id, cause.name)
    if (cell && !this.isPrescribed(cell)) {
      this.setPrescribed(cell, true)
    }
    this.focusGridCell(cause, action)
  }

  onGridCellKeydown(event: KeyboardEvent, cause: TriageTableColumn, action: TriageTableRow) {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault()
      const cell = this.getCell(action.id, cause.name)
      if (cell) this.setPrescribed(cell, !this.isPrescribed(cell))
      this.focusGridCell(cause, action)
      this.scheduleStateRefresh()
      return
    }

    const moves: Record<string, [number, number]> = {
      ArrowUp: [-1, 0],
      ArrowDown: [1, 0],
      ArrowLeft: [0, -1],
      ArrowRight: [0, 1],
    }
    const move = moves[event.key]
    if (!move) return

    event.preventDefault()
    const causes = this.matrixGridRows
      .filter((row): row is Extract<MatrixGridRow, { kind: 'cause' }> => row.kind === 'cause')
      .map(row => row.cause)
    const rowIndex = Math.min(Math.max(causes.indexOf(cause) + move[0], 0), causes.length - 1)
    const columnIndex = Math.min(
      Math.max(this.matrixVisibleActions.indexOf(action) + move[1], 0),
      this.matrixVisibleActions.length - 1,
    )
    const nextCause = causes[rowIndex]
    const nextAction = this.matrixVisibleActions[columnIndex]
    this.focusGridCell(nextCause, nextAction)
    setTimeout(() => document.getElementById(this.getGridCellId(nextCause, nextAction))?.focus())
  }

  getGridCellId(cause: TriageTableColumn, action: TriageTableRow): string {
    const causeIndex = this.actionsColumns.indexOf(cause)
    const actionIndex = this.allActions.indexOf(action)
    return `rs-grid-cell-${causeIndex}-${actionIndex}`
  }

  countBulkCopyTargets(axis: BulkCopyAxis): number {
    return this.getBulkCopyTargets(axis).length
  }

  applySelectionTo(axis: BulkCopyAxis) {
    const source = this.selectedCell
    const targets = this.getBulkCopyTargets(axis)
    if (!source || !targets.length) return

    const { deadline, nextCycle, repeat, shouldReduceRisk, deactivateCriticalArea } = source.value
    targets.forEach(cell => cell.patchValue({ deadline, nextCycle, repeat, shouldReduceRisk, deactivateCriticalArea }))
    this.bulkCopyNotice = `Copied to ${targets.length} ${targets.length === 1 ? 'cell' : 'cells'}.`
  }

  requestCopyLevel(sourceLevelId: string) {
    if (!sourceLevelId || sourceLevelId === this.activeLevel?.id) return
    this.copyLevelSourceId = sourceLevelId
    this.pendingConfirmation = 'copyLevel'
  }

  private copyLevelPrescriptions() {
    const source = this.matrixRows.get(this.copyLevelSourceId)
    const target = this.matrixRows.get(this.activeLevel?.id)
    this.copyLevelSourceId = null
    if (!source || !target) return

    target.forEach((row, actionId) => {
      const sourceRow = source.get(actionId)
      if (sourceRow) {
        row.patchValue(sourceRow.getRawValue())
      }
    })
    this.refreshState()
  }

  private focusGridCell(cause: TriageTableColumn, action: TriageTableRow) {
    this.matrixSelection = { causeName: cause.name, actionId: action.id }
    this.matrixCauseName = cause.name
    this.bulkCopyNotice = null
  }

  private getBulkCopyTargets(axis: BulkCopyAxis): FormGroup[] {
    const selection = this.matrixSelection
    const source = this.selectedCell
    if (!selection || !this.isPrescribed(source)) return []

    const cells = axis === 'row'
      ? this.allActions.map(action => this.getCell(action.id, selection.causeName))
      : this.actionsColumns.map(cause => this.getCell(selection.actionId, cause.name))
    return cells.filter(cell => cell && cell !== source && this.isPrescribed(cell))
  }

  private initMatrixStructure() {
    this.matrixActionGroups = [
      { id: 'inspection' as const, name: 'Improve Inspections', actions: this.inspectionActions },
      { id: 'mitigation' as const, name: 'Mitigation Actions', actions: this.mitigationActions },
      { id: 'repair' as const, name: 'Prescribe Repairs', actions: this.repairActions },
    ].filter(group => group.actions?.length)

    const metricCount = this.integrityMetrics?.length ?? 0
    this.matrixCauseGroups = [
      { id: 'structural', name: 'Structural defects', causes: this.actionsColumns.slice(0, STRUCTURAL_DEFECT_COUNT) },
      {
        id: 'metrics',
        name: 'Integrity metrics',
        causes: this.actionsColumns.slice(STRUCTURAL_DEFECT_COUNT, STRUCTURAL_DEFECT_COUNT + metricCount),
      },
      { id: 'checklist', name: 'Checklist items', causes: this.actionsColumns.slice(STRUCTURAL_DEFECT_COUNT + metricCount) },
    ].filter(group => group.causes.length)

    this.actionNames = new Map(this.allActions.map(action => [action.id, action.actionName]))
    this.matrixCauseName = this.actionsColumns[0]?.name ?? null
    this.exclusionSourceId = this.allActions[0]?.id ?? null
    this.rebuildMatrixGrid()
  }

  private rebuildMatrixGrid() {
    this.matrixVisibleActions = this.matrixActionGroups
      .filter(group => this.matrixActionFilter === 'all' || group.id === this.matrixActionFilter)
      .flatMap(group => group.actions)

    const rows: MatrixGridRow[] = []
    let index = 0
    this.matrixCauseGroups.forEach(group => {
      rows.push({ kind: 'group', group })
      if (!this.collapsedCauseGroups.has(group.id)) {
        group.causes.forEach(cause => rows.push({ kind: 'cause', cause, index: index++ }))
      }
    })
    this.matrixGridRows = rows

    const selection = this.matrixSelection
    const selectionVisible = !!selection
      && this.matrixVisibleActions.some(action => action.id === selection.actionId)
      && rows.some(row => row.kind === 'cause' && row.cause.name === selection.causeName)
    if (!selectionVisible) {
      this.matrixSelection = null
    }
  }

  private indexMatrixRows() {
    this.matrixRows = new Map()
    const tabs = [...this.inspectionActionsFormsTab, ...this.mitigationActionsFormsTab, ...this.repairActionsFormsTab]
    tabs.forEach(tab => {
      const rows = this.matrixRows.get(tab.riskLevelConfigId) ?? new Map<string, FormGroup>()
      tab.table.forEach(row => rows.set(row.get('rowId').value, row))
      this.matrixRows.set(tab.riskLevelConfigId, rows)
    })
  }

  private countLevelPrescriptions(): Record<string, number> {
    const counts: Record<string, number> = {}
    this.matrixRows.forEach((rows, levelId) => {
      let count = 0
      rows.forEach(row => {
        Object.entries(row.value).forEach(([name, cell]: [string, ColumnValue | string]) => {
          if (name !== 'rowId' && (cell as ColumnValue).value) count++
        })
      })
      counts[levelId] = count
    })
    return counts
  }

  // Action exclusions

  getActionName(actionId: string): string {
    return this.actionNames.get(actionId) ?? actionId
  }

  countExclusions(actionId: string): number {
    return this.actionExclusionGroups.find(group => group.prescribedActionId === actionId)?.excludedActionIds.length ?? 0
  }

  isMutualExclusion(actionId: string, otherActionId: string): boolean {
    return this.isActionExcluded(actionId, otherActionId) && this.isActionExcluded(otherActionId, actionId)
  }

  formatExcludedActions(actionIds: string[]): string {
    return actionIds.map(actionId => this.getActionName(actionId)).join(', ')
  }

  // Repair settings

  get repairWarningCount(): number {
    return this.repairCriteriaGroups
      .flatMap(group => group.fields)
      .filter(field => this.getCriterionWarning(field))
      .length
  }

  getCriterionRange(field: RepairCriterionField): string | null {
    return field.max === undefined ? null : `${field.min} to ${field.max}`
  }

  getCriterionWarning(field: RepairCriterionField): string | null {
    const raw = this.repairCriteriaForms?.get(field.control)?.value
    if (raw === '' || raw === null || raw === undefined) return null
    const value = Number(raw)
    if (Number.isNaN(value)) return 'Enter a number.'
    if (value < field.min) return field.max === undefined ? `Use ${field.min} or more.` : `Use ${field.min} to ${field.max}.`
    if (field.max !== undefined && value > field.max) return `Use ${field.min} to ${field.max}.`
    return null
  }

  setRenewThickness(value: string) {
    this.renewThicknessForms.get('renewThicknessControl').setValue(value)
    this.onRepairSettingsChange({ value })
  }

  // Scope

  get scopeSectionTotals(): { selected: number; total: number } {
    const regions = this._AutomaticPoints?.GeneralSettings.Regions ?? []
    const sections = regions.flatMap(region => region.children ?? [])
    return { selected: sections.filter(section => section.selected).length, total: sections.length }
  }
}
